import * as THREE from "three";
import { floorplannerStore } from "./floorplannerStore";
import { AlignmentLineType, LineType, RulerLineType, SingleLineType, WallConnectionEnd, WallConnectionStart, WallType, WallEdgeConnection, WallEdgePoint, VoidFillMetadata, isWallType } from "../types/floorplannerTypes";
import { renderStore } from "./renderStore";
import { calculateWallEdgePoint, createEdgeConnection, getWallAngle, calculateGapDistance, getClosestEndpoint, getClosestPosition } from "../utils/edgeConnectionHelpers";
import { getQuadraticBezierPoint, getQuadraticBezierTangent, transformArcControlPoint } from "../utils/arcUtils";
import type { EditorStoreType } from "./editorStore";
import { jointDetector } from "../utils/JointDetector";
import { calculateWallImaginaryLines, getEndpointImaginaryIntersections, intersectImaginaryLines } from "../utils/imaginaryLineHelpers";
import { isFloorplannerDebugLoggingEnabled } from "../utils/debugLogging";

let editorStore: EditorStoreType | null = null;
let editorStoreLookupWarned = false;

/**
 * Lazily resolves the editorStore singleton from the global cache to avoid
 * introducing a direct ESM import and the circular dependency that comes with it.
 */
const getEditorStore = (): EditorStoreType | null => {
  if (!editorStore) {
    const globalEditorStore = (globalThis as typeof globalThis & { __boniEditorStore?: EditorStoreType }).__boniEditorStore;
    if (globalEditorStore) {
      editorStore = globalEditorStore;
    } else if (!editorStoreLookupWarned && isFloorplannerDebugLoggingEnabled()) {
      console.warn("editorStore is not yet available when requested from snapToOtherObjects");
      editorStoreLookupWarned = true;
    }
  }
  return editorStore;
};

const getInteractionSnappingTolerance = (
  store: EditorStoreType | null = getEditorStore(),
  baseTolerance?: number,
): number => {
  const fallback = baseTolerance ?? store?.snappingTolerance ?? 0.15;
  const helper = (store as unknown as { interactionSnappingTolerance?: (baseTolerance?: number) => number } | null)
    ?.interactionSnappingTolerance;
  return typeof helper === "function" ? helper.call(store, fallback) : fallback;
};

const cloneWallWithEndpoint = (
  wall: WallType,
  endpoint: "start" | "end",
  position: THREE.Vector2,
): WallType => {
  const nextStart = endpoint === "start" ? position.clone() : wall.start.clone();
  const nextEnd = endpoint === "end" ? position.clone() : wall.end.clone();
  const clonedWall: WallType = {
    ...wall,
    start: nextStart,
    end: nextEnd,
    controlPoint: wall.controlPoint
      ? transformArcControlPoint(
        wall.controlPoint,
        wall.start,
        wall.end,
        nextStart,
        nextEnd,
      )
      : undefined,
  };
  return clonedWall;
};

const cloneWallWithGeometry = (
  wall: WallType,
  overrides: {
    start: THREE.Vector2;
    end: THREE.Vector2;
    controlPoint?: THREE.Vector2;
  },
): WallType => {
  return {
    ...wall,
    start: overrides.start.clone(),
    end: overrides.end.clone(),
    controlPoint: overrides.controlPoint
      ? overrides.controlPoint.clone()
      : wall.controlPoint?.clone(),
  };
};

const toVector2 = (point: THREE.Vector2 | { x: number; y: number }): THREE.Vector2 => {
  return point instanceof THREE.Vector2 ? point.clone() : new THREE.Vector2(point.x, point.y);
};

type WallEndpointCorners = {
  polygon: THREE.Vector2[];
  startCorners: [THREE.Vector2, THREE.Vector2];
  endCorners: [THREE.Vector2, THREE.Vector2];
};

const buildWallEndpointCorners = (wall: WallType): WallEndpointCorners | null => {
  const width = wall.wallWidth ?? floorplannerStore.wallWidth;
  const clonePoint = (point: THREE.Vector2 | { x: number; y: number }) =>
    point instanceof THREE.Vector2 ? point.clone() : new THREE.Vector2(point.x, point.y);
  const start = clonePoint(wall.start);
  const end = clonePoint(wall.end);
  const direction = new THREE.Vector2().subVectors(end, start);
  const length = direction.length();

  if (!width || width <= 0 || length < 1e-6) {
    return null;
  }

  // Arc walls: sample the Bezier curve so the polygon and endpoint corners follow
  // the actual visual outline. This matches the rendering in calculateLineVertices
  // (tangent-based perpendicular at every sample, including the endpoints), so
  // snapping, containment checks, and rendering all agree on where the wall's
  // corners actually are.
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const controlPoint = clonePoint(wall.controlPoint);
    const halfWidth = width / 2;
    const sampleCount = 24;
    const leftPoints: THREE.Vector2[] = [];
    const rightPoints: THREE.Vector2[] = [];

    for (let i = 0; i <= sampleCount; i++) {
      const t = i / sampleCount;
      const pointOnCurve = getQuadraticBezierPoint(t, start, controlPoint, end);
      const tangent = getQuadraticBezierTangent(t, start, controlPoint, end);
      const tangentLength = tangent.length();
      const tangentDir = tangentLength > 1e-6
        ? tangent.multiplyScalar(1 / tangentLength)
        : direction.clone().normalize();
      const perpendicular = new THREE.Vector2(-tangentDir.y, tangentDir.x);

      leftPoints.push(pointOnCurve.clone().addScaledVector(perpendicular, halfWidth));
      rightPoints.push(pointOnCurve.clone().addScaledVector(perpendicular, -halfWidth));
    }

    return {
      polygon: [...leftPoints, ...rightPoints.slice().reverse()],
      startCorners: [leftPoints[0], rightPoints[0]],
      endCorners: [leftPoints[leftPoints.length - 1], rightPoints[rightPoints.length - 1]],
    };
  }

  direction.normalize();
  const perpendicular = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(width / 2);

  const startLeft = start.clone().add(perpendicular);
  const startRight = start.clone().sub(perpendicular);
  const endLeft = end.clone().add(perpendicular);
  const endRight = end.clone().sub(perpendicular);

  return {
    polygon: [startLeft, endLeft, endRight, startRight],
    startCorners: [startLeft, startRight],
    endCorners: [endLeft, endRight],
  };
};

const getEndpointCorners = (
  wall: WallType,
  endpoint: "start" | "end",
): [THREE.Vector2, THREE.Vector2] | null => {
  const corners = buildWallEndpointCorners(wall);
  if (!corners) return null;
  return endpoint === "start" ? corners.startCorners : corners.endCorners;
};

const getWallPolygon = (wall: WallType): THREE.Vector2[] | null => {
  const corners = buildWallEndpointCorners(wall);
  return corners?.polygon ?? null;
};

const distanceToSegment = (point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): number => {
  return point.distanceTo(closestPointOnSegment(point, a, b));
};

const closestPointOnSegment = (point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): THREE.Vector2 => {
  const ab = new THREE.Vector2().subVectors(b, a);
  const ap = new THREE.Vector2().subVectors(point, a);
  const abLengthSq = ab.lengthSq();
  if (abLengthSq === 0) {
    return a.clone();
  }
  const t = THREE.MathUtils.clamp(ap.dot(ab) / abLengthSq, 0, 1);
  return new THREE.Vector2(a.x + ab.x * t, a.y + ab.y * t);
};

const isPointInPolygon = (point: THREE.Vector2, polygon: THREE.Vector2[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersects =
      (yi > point.y) !== (yj > point.y) &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
};

const isPointInsideOrOnPolygon = (point: THREE.Vector2, polygon: THREE.Vector2[], tolerance: number): boolean => {
  // Robust convex check (walls are rectangles): all cross products should have the same sign
  let sign = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const edge = new THREE.Vector2().subVectors(b, a);
    const toPoint = new THREE.Vector2().subVectors(point, a);
    const cross = edge.x * toPoint.y - edge.y * toPoint.x;

    if (Math.abs(cross) <= tolerance) {
      // On the edge within tolerance
      if (distanceToSegment(point, a, b) <= tolerance) {
        continue;
      }
    }

    const currentSign = cross > 0 ? 1 : -1;
    if (sign === 0) {
      sign = currentSign;
    } else if (currentSign !== sign) {
      return false;
    }
  }

  // If sign was never set, polygon is degenerate; treat as outside
  return sign !== 0;
};

const minDistanceToPolygon = (point: THREE.Vector2, polygon: THREE.Vector2[]): number => {
  let minDist = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const dist = distanceToSegment(point, a, b);
    if (dist < minDist) {
      minDist = dist;
    }
  }
  return minDist;
};

const areEndpointCornersEmbedded = (
  corners: [THREE.Vector2, THREE.Vector2],
  polygon: THREE.Vector2[],
  tolerance: number,
): boolean => {
  return (
    isPointInsideOrOnPolygon(corners[0], polygon, tolerance) &&
    isPointInsideOrOnPolygon(corners[1], polygon, tolerance)
  );
};

const isEndpointFullyEmbedded = (
  endpoint: THREE.Vector2,
  corners: [THREE.Vector2, THREE.Vector2],
  polygon: THREE.Vector2[],
  tolerance: number,
): boolean => {
  const minEdgeDist = minDistanceToPolygon(endpoint, polygon);
  return (
    isPointInsideOrOnPolygon(endpoint, polygon, tolerance) &&
    minEdgeDist <= tolerance &&
    areEndpointCornersEmbedded(corners, polygon, tolerance)
  );
};

const isPointInsideWallRect = (
  wall: WallType,
  point: THREE.Vector2,
  toleranceWidth: number,
  toleranceLength?: number
): boolean => {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    const polygon = getWallPolygon(wall);
    if (!polygon || polygon.length < 3) {
      return false;
    }
    const tolerance = Math.max(toleranceWidth, toleranceLength ?? toleranceWidth);
    return isPointInPolygon(point, polygon) || minDistanceToPolygon(point, polygon) <= tolerance;
  }

  const width = wall.wallWidth ?? floorplannerStore.wallWidth;
  const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = dir.length();
  if (length < 1e-6 || width <= 0) return false;

  // Default length tolerance to width tolerance if not specified
  const tolL = toleranceLength ?? toleranceWidth;
  const tolW = toleranceWidth;

  const u = dir.clone().normalize();
  const v = new THREE.Vector2(-u.y, u.x);
  const rel = new THREE.Vector2().subVectors(point, wall.start);
  const projAlong = rel.dot(u);
  const projPerp = rel.dot(v);

  return (
    projAlong >= -tolL &&
    projAlong <= length + tolL &&
    Math.abs(projPerp) <= width / 2 + tolW
  );
};

const areWallsNearlyCollinear = (wallA: WallType, wallB: WallType, angleThreshold: number = 0.05): boolean => {
  const angleA = getWallAngle(wallA);
  const angleB = getWallAngle(wallB);
  let diff = Math.abs(angleA - angleB) % Math.PI;
  if (diff > Math.PI) {
    diff -= Math.PI;
  }
  const collinearDiff = Math.min(diff, Math.abs(Math.PI - diff));
  return collinearDiff < angleThreshold;
};

const getEndpointPosition = (wall: WallType, endpoint: "start" | "end"): THREE.Vector2 => {
  return endpoint === "start" ? wall.start : wall.end;
};

type WallEndpoint = "start" | "end";

type EffectiveEndpointSnap = {
  endpoint: WallEndpoint;
  left: THREE.Vector2;
  right: THREE.Vector2;
  distance: number;
  otherWallId: string;
};

const EFFECTIVE_ENDPOINT_EXTENSION = 5;

const isFiniteVector2 = (point: THREE.Vector2): boolean => Number.isFinite(point.x) && Number.isFinite(point.y);

const getConnectionEndpointForWall = (connection: WallEdgeConnection, wallId: string): "start" | "end" | "middle" | null => {
  if (connection.sourceWallId === wallId) {
    return connection.sourceEndpoint;
  }
  if (connection.targetWallId === wallId) {
    return connection.targetEndpoint;
  }
  return null;
};

const getOtherWallIdFromConnection = (connection: WallEdgeConnection, wallId: string): string | null => {
  if (connection.sourceWallId === wallId) {
    return connection.targetWallId;
  }
  if (connection.targetWallId === wallId) {
    return connection.sourceWallId;
  }
  return null;
};

const collectEndpointConnectionsForWall = (
  wall: WallType,
  endpoint: WallEndpoint,
): Array<{
  connection: WallEdgeConnection;
  otherWall: WallType;
  otherEndpoint: "start" | "end" | "middle" | null;
}> => {
  const matches: Array<{
    connection: WallEdgeConnection;
    otherWall: WallType;
    otherEndpoint: "start" | "end" | "middle" | null;
  }> = [];
  const seen = new Set<string>();

  const addIfMatches = (connection: WallEdgeConnection) => {
    const connectionEndpoint = getConnectionEndpointForWall(connection, wall.id);
    if (connectionEndpoint !== endpoint) {
      if (connectionEndpoint !== "middle") {
        return;
      }

      const wallEdgePoint = connection.sourceWallId === wall.id
        ? connection.sourceEdgePoint
        : connection.targetEdgePoint;
      const endpointPosition = getEndpointPosition(wall, endpoint);
      const endpointTolerance = (wall.wallWidth ?? floorplannerStore.wallWidth) + 1e-6;
      if (!wallEdgePoint || toVector2(wallEdgePoint).distanceTo(endpointPosition) > endpointTolerance) {
        return;
      }
    }

    const otherWallId = getOtherWallIdFromConnection(connection, wall.id);
    const otherWall = otherWallId ? floorplannerStore.wallsMap.get(otherWallId) : undefined;
    if (!otherWall) {
      return;
    }

    const key = connection.sourceWallId + ":" + connection.sourceEndpoint + "->" + connection.targetWallId + ":" + connection.targetEndpoint;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    matches.push({
      connection,
      otherWall,
      otherEndpoint: getConnectionEndpointForWall(connection, otherWall.id),
    });
  };

  wall.edgeConnections?.forEach(addIfMatches);

  for (const otherWall of floorplannerStore.wallsMap.values()) {
    if (otherWall.id === wall.id) {
      continue;
    }
    otherWall.edgeConnections?.forEach(addIfMatches);
  }

  return matches;
};

const getEffectiveEndpointCornerPairs = (
  wall: WallType,
  endpoint: WallEndpoint,
): Array<{
  left: THREE.Vector2;
  right: THREE.Vector2;
  otherWallId: string;
}> => {
  const pairs: Array<{
    left: THREE.Vector2;
    right: THREE.Vector2;
    otherWallId: string;
  }> = [];
  const fallbackWidth = wall.wallWidth ?? floorplannerStore.wallWidth;

  const addPair = (left: THREE.Vector2, right: THREE.Vector2, otherWallId: string) => {
    if (!isFiniteVector2(left) || !isFiniteVector2(right)) {
      return;
    }

    const duplicate = pairs.some(
      (pair) => pair.otherWallId === otherWallId && pair.left.distanceTo(left) < 1e-4 && pair.right.distanceTo(right) < 1e-4,
    );
    if (duplicate) {
      return;
    }

    pairs.push({
      left: left.clone(),
      right: right.clone(),
      otherWallId,
    });
  };

  for (const { otherWall, otherEndpoint } of collectEndpointConnectionsForWall(wall, endpoint)) {
    const intersections = getEndpointImaginaryIntersections(wall, endpoint, otherWall, fallbackWidth, {
      forceSharedEndpoint: otherEndpoint === "start" || otherEndpoint === "end",
    });

    if (intersections) {
      addPair(intersections.left, intersections.right, otherWall.id);
    }

    const wallLines = calculateWallImaginaryLines(wall, fallbackWidth, EFFECTIVE_ENDPOINT_EXTENSION, false, endpoint);
    const otherLines = calculateWallImaginaryLines(
      otherWall,
      otherWall.wallWidth ?? floorplannerStore.wallWidth,
      EFFECTIVE_ENDPOINT_EXTENSION,
      false,
      otherEndpoint === "start" || otherEndpoint === "end" ? otherEndpoint : undefined,
    );

    for (const otherLine of [otherLines.left, otherLines.right]) {
      const leftIntersection = intersectImaginaryLines(wallLines.left, otherLine);
      const rightIntersection = intersectImaginaryLines(wallLines.right, otherLine);
      if (leftIntersection && rightIntersection) {
        addPair(leftIntersection.point, rightIntersection.point, otherWall.id);
      }
    }
  }

  return pairs;
};

const distanceToEffectiveEndpointPair = (
  point: THREE.Vector2,
  pair: { left: THREE.Vector2; right: THREE.Vector2 },
): number => {
  return Math.min(point.distanceTo(pair.left), point.distanceTo(pair.right), distanceToSegment(point, pair.left, pair.right));
};

const findEffectiveEndpointSnap = (
  targetWall: WallType,
  referencePosition: THREE.Vector2,
  tolerance: number,
): EffectiveEndpointSnap | null => {
  let best: EffectiveEndpointSnap | null = null;

  for (const endpoint of ["start", "end"] as WallEndpoint[]) {
    const pairs = getEffectiveEndpointCornerPairs(targetWall, endpoint);
    for (const pair of pairs) {
      const distance = distanceToEffectiveEndpointPair(referencePosition, pair);
      if (distance > tolerance) {
        continue;
      }
      if (!best || distance < best.distance) {
        best = {
          endpoint,
          left: pair.left.clone(),
          right: pair.right.clone(),
          distance,
          otherWallId: pair.otherWallId,
        };
      }
    }
  }

  return best;
};

const buildWallPolygonWithEffectiveEndpoint = (
  wall: WallType,
  endpoint: WallEndpoint,
  effectiveEndpoint: Pick<EffectiveEndpointSnap, "left" | "right">,
): THREE.Vector2[] | null => {
  const corners = buildWallEndpointCorners(wall);
  if (!corners) {
    return null;
  }

  return endpoint === "start"
    ? [
      effectiveEndpoint.left.clone(),
      corners.endCorners[0].clone(),
      corners.endCorners[1].clone(),
      effectiveEndpoint.right.clone(),
    ]
    : [
      corners.startCorners[0].clone(),
      effectiveEndpoint.left.clone(),
      effectiveEndpoint.right.clone(),
      corners.startCorners[1].clone(),
    ];
};

const connectionEmbeddingPassWithEffectiveTarget = (
  sourceWall: WallType,
  sourceEndpoint: WallEndpoint,
  targetWall: WallType,
  effectiveTarget: EffectiveEndpointSnap,
  tolerance: number,
): { pass: boolean; sourceInTarget: boolean; targetInSource: boolean } => {
  const sourceCorners = getEndpointCorners(sourceWall, sourceEndpoint);
  const sourcePolygon = getWallPolygon(sourceWall);
  const targetPolygon = buildWallPolygonWithEffectiveEndpoint(targetWall, effectiveTarget.endpoint, effectiveTarget);

  const sourceInTarget = !!sourceCorners && !!targetPolygon && areEndpointCornersEmbedded(sourceCorners, targetPolygon, tolerance);
  const targetInSource = !!sourcePolygon && areEndpointCornersEmbedded([effectiveTarget.left, effectiveTarget.right], sourcePolygon, tolerance);

  return {
    pass: sourceInTarget || targetInSource,
    sourceInTarget,
    targetInSource,
  };
};

const hasEffectiveEndpointTouch = (
  sourceWall: WallType,
  sourceEndpoint: WallEndpoint,
  targetWall: WallType,
  effectiveTarget: EffectiveEndpointSnap,
  tolerance: number,
): boolean => {
  const sourceCorners = getEndpointCorners(sourceWall, sourceEndpoint);
  const sourcePolygon = getWallPolygon(sourceWall);
  const targetPolygon = buildWallPolygonWithEffectiveEndpoint(targetWall, effectiveTarget.endpoint, effectiveTarget);

  const countNearPolygon = (corners: [THREE.Vector2, THREE.Vector2], polygon: THREE.Vector2[]): number => {
    return corners.filter(
      (corner) => isPointInsideOrOnPolygon(corner, polygon, tolerance) || minDistanceToPolygon(corner, polygon) <= tolerance,
    ).length;
  };

  const sourceNearTarget = sourceCorners && targetPolygon ? countNearPolygon(sourceCorners, targetPolygon) : 0;
  const targetNearSource = sourcePolygon ? countNearPolygon([effectiveTarget.left, effectiveTarget.right], sourcePolygon) : 0;

  return sourceNearTarget > 0 || targetNearSource > 0;
};

const getEffectiveTargetEdgePointForConnection = (
  connection: WallEdgeConnection,
  effectiveTarget: EffectiveEndpointSnap | null,
): THREE.Vector2 | null => {
  if (!effectiveTarget || connection.targetEndpoint !== effectiveTarget.endpoint) {
    return null;
  }

  const sourceEdgePoint = toVector2(connection.sourceEdgePoint);
  const candidates: THREE.Vector2[] = [
    closestPointOnSegment(sourceEdgePoint, effectiveTarget.left, effectiveTarget.right),
  ];

  if (connection.targetSide) {
    candidates.push(connection.targetSide === "left" ? effectiveTarget.left.clone() : effectiveTarget.right.clone());
  }

  return candidates.sort((a, b) => sourceEdgePoint.distanceTo(a) - sourceEdgePoint.distanceTo(b))[0] ?? null;
};

const applyEffectiveEndpointSnapToConnection = (
  connection: WallEdgeConnection,
  effectiveTarget: EffectiveEndpointSnap | null,
): WallEdgeConnection => {
  const effectiveTargetEdgePoint = getEffectiveTargetEdgePointForConnection(connection, effectiveTarget);
  if (!effectiveTargetEdgePoint) {
    return connection;
  }

  const sourceEdgePoint = toVector2(connection.sourceEdgePoint);
  const actualTargetEdgePoint = toVector2(connection.targetEdgePoint);
  const effectiveGapDistance = sourceEdgePoint.distanceTo(effectiveTargetEdgePoint);
  const actualGapDistance = sourceEdgePoint.distanceTo(actualTargetEdgePoint);

  if (effectiveGapDistance + 1e-6 >= actualGapDistance) {
    return connection;
  }

  return {
    ...connection,
    targetEdgePoint: effectiveTargetEdgePoint,
    gapDistance: effectiveGapDistance,
  };
};

const findExistingVoidFillMetadata = (sourceWall: WallType, targetWall: WallType): VoidFillMetadata | null => {
  const candidates = [
    ...(sourceWall.edgeConnections || []),
    ...(targetWall.edgeConnections || []),
  ];

  for (const conn of candidates) {
    if (conn.voidFill &&
      ((conn.sourceWallId === sourceWall.id && conn.targetWallId === targetWall.id) ||
        (conn.sourceWallId === targetWall.id && conn.targetWallId === sourceWall.id))) {
      return conn.voidFill;
    }
  }

  return null;
};

/**
 * Clear VoidFill metadata from all participating walls when a joint becomes a multi-joint.
 * This is called when a 3rd wall joins an existing 2-wall connection, upgrading it to a multi-joint.
 * The multi-joint system will handle the gap filling instead of the 2-wall VoidFill system.
 * 
 * @param wallIds - Array of wall IDs participating in the multi-joint
 * @param endpoint - The endpoint where the multi-joint is formed (for the source wall)
 */
export const clearVoidFillForMultiJoint = (wallIds: string[], endpoint: 'start' | 'end'): void => {
  // Clear voidFill from ALL edge connections between walls in this joint
  // This handles both directions: source->target and target->source
  for (const wallId of wallIds) {
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) continue;

    // Clear voidFill from edge connections
    if (wall.edgeConnections) {
      wall.edgeConnections.forEach(ec => {
        // Clear voidFill if this edge connection connects to another wall in the multi-joint
        const connectsToMultiJointWall = wallIds.includes(ec.targetWallId);
        if (connectsToMultiJointWall && ec.voidFill) {
          delete ec.voidFill;
        }
      });
    }

    // IMPORTANT: Also clear any stale CENTER connections between walls in the multi-joint
    // These can be created inadvertently by updateWallConnections and cause rendering issues
    // Multi-joints should use EDGE connections, not center connections
    if (wall.centerConnections) {
      const originalLength = wall.centerConnections.length;
      wall.centerConnections = wall.centerConnections.filter(cc => {
        // Keep connections that are NOT to other walls in this multi-joint
        return !wallIds.includes(cc.id) || cc.id === wallId;
      });
      if (wall.centerConnections.length < originalLength) {
        // Center connections were removed
      }
    }
  }

  // Invalidate the joint detector cache so it re-detects the multi-joint
  jointDetector.invalidateCache();

  // CRITICAL: Also invalidate the render store's joint cache for all affected walls
  // This clears the old 2-wall joint polygons so the multi-joint polygon can be generated
  for (const wallId of wallIds) {
    renderStore.invalidateJointsForWall(wallId, 'clearVoidFillForMultiJoint', {
      scheduleAffectedWallUpdates: false,
    });
  }
};

const hasSingleCornerEmbedded = (
  corners: [THREE.Vector2, THREE.Vector2],
  polygon: THREE.Vector2[],
  tolerance: number
): boolean => {
  const embeddedFlags = corners.map(corner => isPointInsideOrOnPolygon(corner, polygon, tolerance));
  return embeddedFlags.filter(Boolean).length === 1;
};

const vecToObj = (v: THREE.Vector2) => ({ x: Number(v.x.toFixed(4)), y: Number(v.y.toFixed(4)) });

const detectSingleCornerTouchConnection = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end" | "middle",
  tolerance: number,
  snappingTolerance: number
): VoidFillMetadata | null => {
  if (renderStore.isDragging) {
    return null;
  }
  const editor = getEditorStore();
  const debugLog = editor?.isDebugModeActive || editor?.debugJointPolygons;
  const effectiveTolerance = Math.max(tolerance, snappingTolerance * 0.5);

  // CHECK: Would this connection create a multi-joint (3+ walls)?
  // If so, let the multi-joint system handle it - no VoidFill needed.
  // We check the target endpoint since that's where the joint would form.
  if (targetEndpoint === 'start' || targetEndpoint === 'end') {
    const wouldCreateMultiJoint = jointDetector.wouldCreateMultiJoint(
      targetWall.id,
      targetEndpoint,
      sourceWall.id
    );
    if (wouldCreateMultiJoint) {
      // Multi-joint detected - let multi-joint system handle rendering
      // Don't create VoidFillMetadata for 2-wall style gap filling
      return null;
    }
  }

  // Only applies to endpoint-style connections that are not collinear
  if (areWallsNearlyCollinear(sourceWall, targetWall)) {
    return null;
  }

  let resolvedTargetEndpoint: "start" | "end" | "middle" = targetEndpoint;
  if (targetEndpoint === "middle") {
    // Try to resolve middle to an endpoint for corner-touch cases, but only if very close to an endpoint
    const sourceEndpointPos = getEndpointPosition(sourceWall, sourceEndpoint);
    const distToStart = sourceEndpointPos.distanceTo(targetWall.start);
    const distToEnd = sourceEndpointPos.distanceTo(targetWall.end);
    const targetWidth = targetWall.wallWidth ?? floorplannerStore.wallWidth;
    const sourceWidth = sourceWall.wallWidth ?? floorplannerStore.wallWidth;
    const endpointSnapTolerance = Math.max(snappingTolerance * 2, Math.max(targetWidth, sourceWidth) * 2);
    const dragging = renderStore.isDragging;

    if (!dragging && (distToStart < endpointSnapTolerance || distToEnd < endpointSnapTolerance)) {
      resolvedTargetEndpoint = distToStart <= distToEnd ? "start" : "end";
    } else {
      return null; // true mid-connection, do not attempt void fill
    }
  }

  const sourceCorners = getEndpointCorners(sourceWall, sourceEndpoint);
  const targetCorners = resolvedTargetEndpoint === "start" || resolvedTargetEndpoint === "end" ? getEndpointCorners(targetWall, resolvedTargetEndpoint) : null;
  const sourcePolygon = getWallPolygon(sourceWall);
  const targetPolygon = getWallPolygon(targetWall);

  if (!sourceCorners || !targetCorners || !sourcePolygon || !targetPolygon) {
    return null;
  }

  const countCornersNearPolygon = (corners: [THREE.Vector2, THREE.Vector2], polygon: THREE.Vector2[]): number => {
    let count = 0;
    for (const corner of corners) {
      const inside = isPointInsideOrOnPolygon(corner, polygon, effectiveTolerance);
      const nearEdge = minDistanceToPolygon(corner, polygon) <= effectiveTolerance;
      if (inside || nearEdge) {
        count++;
      }
    }
    return count;
  };

  const sourceNear = countCornersNearPolygon(sourceCorners, targetPolygon);
  const targetNear = countCornersNearPolygon(targetCorners, sourcePolygon);

  // Reject if neither wall has a corner near the other wall
  if (sourceNear === 0 && targetNear === 0) {
    return null;
  }

  // Reject if either wall already has both corners near/inside (handled by normal embedding path)
  if (sourceNear >= 2 || targetNear >= 2) {
    return null;
  }

  // First-create policy: reuse existing metadata if present; otherwise use the attaching wall as owner
  const existingMetadata = findExistingVoidFillMetadata(sourceWall, targetWall);
  const ownerWallId = existingMetadata?.ownerWallId || sourceWall.id;
  const ownerWall = floorplannerStore.wallsMap.get(ownerWallId) || sourceWall;

  return {
    required: true,
    ownerWallId,
    fillColor: existingMetadata?.fillColor ?? ownerWall.fillColor ?? floorplannerStore.fillColor,
    outlineColor: existingMetadata?.outlineColor ?? ownerWall.lineColor ?? floorplannerStore.lineColor,
    outlineWidth: existingMetadata?.outlineWidth ?? ownerWall.lineWeight ?? floorplannerStore.wallLineWeight,
    outlineDashed: existingMetadata?.outlineDashed ?? ownerWall.lineType === LineType.dashed,
    reason: "single-corner",
    resolvedTargetEndpoint,
  };
};



const endpointCornersInsideWall = (
  endpoint: "start" | "end",
  sourceWall: WallType,
  targetWall: WallType,
  tolerance: number,
  context?: string,
): boolean => {
  const corners = getEndpointCorners(sourceWall, endpoint);
  if (!corners) return false;

  // Strict tolerance for both width and length.
  // We reject connections where the wall is not substantially overlapping the target wall.
  // This filters out "Vertex-to-Vertex" touches and "Center-to-Center" overlaps where one corner is out,
  // enforcing the rule that BOTH corners must be within or on the target wall structure.
  const pad = Math.max(tolerance, 0.002);

  const firstInside = isPointInsideWallRect(targetWall, corners[0], pad, pad);
  const secondInside = isPointInsideWallRect(targetWall, corners[1], pad, pad);

  return firstInside && secondInside;
};

const connectionEmbeddingPass = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end" | "middle",
  tolerance: number,
): { pass: boolean; sourceInTarget: boolean; targetInSource: boolean } => {
  const sourceInTarget = endpointCornersInsideWall(sourceEndpoint, sourceWall, targetWall, tolerance, "source->target");

  const targetInSource =
    targetEndpoint === "start" || targetEndpoint === "end"
      ? endpointCornersInsideWall(targetEndpoint, targetWall, sourceWall, tolerance, "target->source")
      : false;

  return { pass: sourceInTarget || targetInSource, sourceInTarget, targetInSource };
};

const satisfiesCornerEmbedding = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end" | "middle",
  tolerance: number,
): boolean => {
  const sourceAgainstTarget = endpointCornersInsideWall(
    sourceEndpoint,
    sourceWall,
    targetWall,
    tolerance,
  );

  const targetAgainstSource =
    targetEndpoint === "start" || targetEndpoint === "end"
      ? endpointCornersInsideWall(
        targetEndpoint,
        targetWall,
        sourceWall,
        tolerance,
      )
      : false;

  return sourceAgainstTarget || targetAgainstSource;
};

/**
 * Soft cap after distance ranking. Must never early-break on Map insertion
 * order: with two pasted floorplans ~0.2m apart, the first 20 hits in the
 * shared gap can all belong to the older plan, so the wall being drawn in the
 * newer plan never sees its own neighbors and can snap across the gap.
 */
const NEARBY_WALLS_MAX_RESULTS = 50;

/**
 * Collect walls within maxDistance of `position`, nearest first.
 * Always ranks by distance so a soft result cap cannot drop closer walls in
 * favor of farther ones that happened to appear earlier in wallsMap.
 */
export const selectNearbyWallsByDistance = (
  walls: Iterable<WallType>,
  position: THREE.Vector2,
  maxDistance: number,
  maxResults: number = NEARBY_WALLS_MAX_RESULTS,
): WallType[] => {
  const scored: { wall: WallType; dist: number }[] = [];
  const query = new THREE.Vector3(position.x, position.y, 0);
  const closest = new THREE.Vector3();

  for (const wall of walls) {
    const seg = new THREE.Line3(
      new THREE.Vector3(wall.start.x, wall.start.y, 0),
      new THREE.Vector3(wall.end.x, wall.end.y, 0),
    );
    seg.closestPointToPoint(query, true, closest);
    const dist = position.distanceTo(new THREE.Vector2(closest.x, closest.y));
    if (dist <= maxDistance) {
      scored.push({ wall, dist });
    }
  }

  scored.sort((a, b) => a.dist - b.dist);
  if (scored.length > maxResults) {
    return scored.slice(0, maxResults).map((entry) => entry.wall);
  }
  return scored.map((entry) => entry.wall);
};

// Spatial partitioning function to limit snapping calculations to nearby walls
const getNearbyWalls = (position: THREE.Vector2, maxDistance: number = 2.0): WallType[] => {
  // Check if there's a wallDragState entry for the current sourceObject and return precomputed set
  const currentDragEntries = Array.from(floorplannerStore.wallDragState.entries());
  if (currentDragEntries.length === 1) {
    // Use the first drag entry for caching (typically there's only one wall being dragged)
    const [, dragState] = currentDragEntries[0];
    if (dragState.nearbyWallIds) {
      const cachedWalls: WallType[] = [];
      for (const wallId of dragState.nearbyWallIds) {
        const wall = floorplannerStore.wallsMap.get(wallId);
        if (wall) {
          cachedWalls.push(wall);
        }
      }
      return cachedWalls;
    }
  }

  return selectNearbyWallsByDistance(
    floorplannerStore.wallsMap.values(),
    position,
    maxDistance,
  );
};

export interface SnappingResult {
  snapped: boolean;
  startPoint?: THREE.Vector2;
  endPoint?: THREE.Vector2;
  position?: THREE.Vector2;
  targetId?: string;
  targetObject?: any;
}

export interface AlignmentSnapResult {
  snapped: boolean;
  snappedX: boolean;
  snappedY: boolean;
  start: THREE.Vector2;
  end: THREE.Vector2;
  targetX?: number;
  targetY?: number;
}

export interface AlignmentSnapState {
  snappedX: boolean;
  snappedY: boolean;
  targetX?: number;
  targetY?: number;
}

type AlignmentSnapCandidate = {
  axis: "x" | "y";
  coordinate: number;
  distance: number;
  line: AlignmentLineType;
};

/**
 * Sticky selection options for alignment target picking.
 */
type ChooseAlignmentTargetsOptions = {
  previous?: AlignmentSnapState;
  applyThreshold: number;
  releaseThreshold: number;
};

const computeWallEdgePointsForSnap = (
  wall: WallType,
  start: THREE.Vector2,
  end: THREE.Vector2,
): {
  startLeft: THREE.Vector2;
  startRight: THREE.Vector2;
  endLeft: THREE.Vector2;
  endRight: THREE.Vector2;
} | null => {
  const chord = end.clone().sub(start);
  if (chord.lengthSq() < 1e-10) {
    return null;
  }
  const chordDir = chord.clone().normalize();
  const width = wall.wallWidth || floorplannerStore.wallWidth || 0;
  const halfWidth = width / 2;

  // Derive per-endpoint tangents using the provided (possibly dragged) start/end
  // together with the wall's own controlPoint. Straight walls fall back to chord.
  const isArc = wall.lineForm === "arc" && wall.controlPoint !== undefined;
  let startDir = chordDir;
  let endDir = chordDir;
  if (isArc && wall.controlPoint) {
    const cp = wall.controlPoint;
    const startTangent = new THREE.Vector2(cp.x - start.x, cp.y - start.y);
    if (startTangent.lengthSq() > 1e-12) {
      startDir = startTangent.normalize();
    }
    const endTangent = new THREE.Vector2(end.x - cp.x, end.y - cp.y);
    if (endTangent.lengthSq() > 1e-12) {
      endDir = endTangent.normalize();
    }
  }

  const startPerp = new THREE.Vector2(-startDir.y, startDir.x);
  const endPerp = new THREE.Vector2(-endDir.y, endDir.x);

  const startLeft = start.clone().add(startPerp.clone().multiplyScalar(halfWidth));
  const startRight = start.clone().add(startPerp.clone().multiplyScalar(-halfWidth));
  const endLeft = end.clone().add(endPerp.clone().multiplyScalar(halfWidth));
  const endRight = end.clone().add(endPerp.clone().multiplyScalar(-halfWidth));

  return { startLeft, startRight, endLeft, endRight };
};

const getAlignmentLineCandidate = (
  alignmentLine: AlignmentLineType,
  edge: THREE.Vector2,
): AlignmentSnapCandidate | null => {
  const editorStore = getEditorStore();
  if (!editorStore) {
    return null;
  }

  const targetLine = new THREE.Line3(
    alignmentLine.axis === "x"
      ? new THREE.Vector3(alignmentLine.hit.x, -1000, 0)
      : new THREE.Vector3(-1000, alignmentLine.hit.y, 0),
    alignmentLine.axis === "x"
      ? new THREE.Vector3(alignmentLine.hit.x, 1000, 0)
      : new THREE.Vector3(1000, alignmentLine.hit.y, 0),
  );

  const closestEdge = new THREE.Vector3();
  targetLine.closestPointToPoint(
    new THREE.Vector3(edge.x, edge.y, 0),
    true,
    closestEdge,
  );

  const closestVector = new THREE.Vector2(closestEdge.x, closestEdge.y);
  const distance = edge.distanceTo(closestVector);

  const snappingTolerance = getInteractionSnappingTolerance(editorStore);
  if (distance < snappingTolerance) {
    return {
      axis: alignmentLine.axis,
      coordinate: alignmentLine.axis === "x" ? alignmentLine.hit.x : alignmentLine.hit.y,
      distance,
      line: alignmentLine,
    };
  }

  return null;
};

const collectAlignmentCandidates = (
  edge: THREE.Vector2,
  object?: WallType | SingleLineType | RulerLineType,
  start?: THREE.Vector2,
  end?: THREE.Vector2,
  snappingEnd?: number,
): AlignmentSnapCandidate[] => {
  const candidates: AlignmentSnapCandidate[] = [];
  const processedIds = new Set<string>();

  // For edge-mode alignment lines with walls, check edge points instead of center
  // This ensures narrow walls (e.g., 100mm) can snap properly
  const checkEdgePoints = 
    object && 
    isWallType(object) && 
    start && 
    end &&
    snappingEnd !== undefined;

  const pointsToCheck: THREE.Vector2[] = [edge];
  
  if (checkEdgePoints) {
    // Add edge points for the snapping end
    const edgePoints = computeWallEdgePointsForSnap(object as WallType, start, end);
    if (edgePoints) {
      const includeStart = snappingEnd === WallConnectionStart || snappingEnd === undefined;
      const includeEnd = snappingEnd === WallConnectionEnd || snappingEnd === undefined;
      
      if (includeStart) {
        pointsToCheck.push(edgePoints.startLeft, edgePoints.startRight);
      }
      if (includeEnd) {
        pointsToCheck.push(edgePoints.endLeft, edgePoints.endRight);
      }
    }
  }

  const addCandidatesFromMap = (lines: Iterable<AlignmentLineType>) => {
    for (const alignmentLine of lines) {
      if (processedIds.has(alignmentLine.id)) {
        continue;
      }
      
      // For edge-mode lines with walls, check all relevant points
      // For center-mode lines or non-walls, just check the center point
      if (alignmentLine.mode === "edge" && checkEdgePoints) {
        // Check all edge points and use the closest one
        let bestCandidate: AlignmentSnapCandidate | null = null;
        let bestDistance = Infinity;
        
        for (const point of pointsToCheck) {
          const candidate = getAlignmentLineCandidate(alignmentLine, point);
          if (candidate && candidate.distance < bestDistance) {
            bestDistance = candidate.distance;
            bestCandidate = candidate;
          }
        }
        
        if (bestCandidate) {
          candidates.push(bestCandidate);
        }
      } else {
        // For center-mode or non-wall objects, use the center point
        const candidate = getAlignmentLineCandidate(alignmentLine, edge);
        if (candidate) {
          candidates.push(candidate);
        }
      }
      
      processedIds.add(alignmentLine.id);
    }
  };

  addCandidatesFromMap(renderStore.alignmentLinesApproxMap.values());
  addCandidatesFromMap(renderStore.alignmentLinesMap.values());

  return candidates;
};

const chooseAlignmentTargets = (
  edge: THREE.Vector2,
  candidates: AlignmentSnapCandidate[],
  options?: ChooseAlignmentTargetsOptions,
): { x?: AlignmentSnapCandidate; y?: AlignmentSnapCandidate } => {
  if (candidates.length === 0) {
    return {};
  }

  const xCandidates: AlignmentSnapCandidate[] = [];
  const yCandidates: AlignmentSnapCandidate[] = [];

  for (const candidate of candidates) {
    if (candidate.axis === "x") {
      xCandidates.push(candidate);
    } else {
      yCandidates.push(candidate);
    }
  }

  let selectedX: AlignmentSnapCandidate | undefined;
  let selectedY: AlignmentSnapCandidate | undefined;

  if (xCandidates.length && yCandidates.length) {
    let bestScore = Infinity;
    for (const xCandidate of xCandidates) {
      for (const yCandidate of yCandidates) {
        const dx = Math.abs(edge.x - xCandidate.coordinate);
        const dy = Math.abs(edge.y - yCandidate.coordinate);
        const score = Math.hypot(dx, dy);
        if (score < bestScore) {
          bestScore = score;
          selectedX = xCandidate;
          selectedY = yCandidate;
        }
      }
    }
  }

  if (!selectedX && xCandidates.length) {
    selectedX = xCandidates.reduce<AlignmentSnapCandidate | undefined>((best, candidate) => {
      if (!best || candidate.distance < best.distance) {
        return candidate;
      }
      return best;
    }, undefined);
  }

  if (!selectedY && yCandidates.length) {
    selectedY = yCandidates.reduce<AlignmentSnapCandidate | undefined>((best, candidate) => {
      if (!best || candidate.distance < best.distance) {
        return candidate;
      }
      return best;
    }, undefined);
  }

  const applyPreviousPreference = (
    current: AlignmentSnapCandidate | undefined,
    axisCandidates: AlignmentSnapCandidate[],
    axis: "x" | "y",
  ): AlignmentSnapCandidate | undefined => {
    if (!options?.previous) {
      return current;
    }

    const previousSnapped =
      axis === "x" ? options.previous.snappedX : options.previous.snappedY;
    const previousTarget =
      axis === "x" ? options.previous.targetX : options.previous.targetY;

    if (!previousSnapped || previousTarget === undefined) {
      return current;
    }

    const prevCandidate = axisCandidates.find(
      (candidate) => Math.abs(candidate.coordinate - previousTarget) < 1e-6,
    );

    if (!prevCandidate) {
      return current;
    }

    const axisValue = axis === "x" ? edge.x : edge.y;
    const prevDistance = Math.abs(axisValue - prevCandidate.coordinate);
    const releaseThreshold = options.releaseThreshold;
    const applyThreshold = options.applyThreshold;

    // If no current candidate, maintain previous snap if within apply threshold
    if (!current) {
      return prevDistance <= applyThreshold ? prevCandidate : undefined;
    }

    // If previous and current are the same candidate, keep it
    if (prevCandidate === current) {
      return current;
    }

    // Hysteresis logic: Once snapped, maintain the snap until clearly beyond release threshold
    // This prevents toggling when distances are close
    const currentDistance = Math.abs(axisValue - current.coordinate);
    
    // Priority 1: If current candidate is within apply threshold, always allow it
    // This ensures we can snap to new alignment lines when approaching them
    if (currentDistance <= applyThreshold) {
      // If we're still close to previous snap (within release threshold), use hysteresis
      // to prevent toggling between similar distances
      if (prevDistance <= releaseThreshold) {
        // Only maintain previous if distances are very similar (within 20% of each other)
        // This prevents toggling while still allowing snaps to closer targets
        if (prevDistance <= currentDistance * 1.2) {
          return prevCandidate;
        }
      }
      // Current is clearly better or we're beyond release threshold, use current
      return current;
    }
    
    // Priority 2: Current is not within apply threshold, but maintain previous if still close
    // This creates the "sticky" behavior - once snapped, stay snapped until beyond release
    if (prevDistance <= releaseThreshold) {
      return prevCandidate;
    }
    
    // Beyond both thresholds, no snap
    return undefined;
  };

  if (xCandidates.length) {
    selectedX = applyPreviousPreference(selectedX, xCandidates, "x");
  }

  if (yCandidates.length) {
    selectedY = applyPreviousPreference(selectedY, yCandidates, "y");
  }

  return { x: selectedX, y: selectedY };
};

const getEdgeAlignmentDelta = (
  wall: WallType,
  start: THREE.Vector2,
  end: THREE.Vector2,
  axis: "x" | "y",
  targetCoordinate: number,
  snappingEnd: number,
): { delta: number; distance: number } | null => {
  const edgePoints = computeWallEdgePointsForSnap(wall, start, end);
  if (!edgePoints) {
    return null;
  }

  const includeStart = snappingEnd === WallConnectionStart;
  const includeEnd = snappingEnd === WallConnectionEnd;
  const axisKey: "x" | "y" = axis;

  const candidateCoords: number[] = [];

  const addEdgeCoords = (point: THREE.Vector2) => {
    candidateCoords.push(point[axisKey]);
  };

  if (includeStart || (!includeStart && !includeEnd)) {
    addEdgeCoords(edgePoints.startLeft);
    addEdgeCoords(edgePoints.startRight);
  }

  if (includeEnd || (!includeStart && !includeEnd)) {
    addEdgeCoords(edgePoints.endLeft);
    addEdgeCoords(edgePoints.endRight);
  }

  if (candidateCoords.length === 0) {
    addEdgeCoords(edgePoints.startLeft);
    addEdgeCoords(edgePoints.startRight);
    addEdgeCoords(edgePoints.endLeft);
    addEdgeCoords(edgePoints.endRight);
  }

  let bestDelta: number | null = null;
  let bestDistance = Infinity;

  for (const coord of candidateCoords) {
    const delta = targetCoordinate - coord;
    const distance = Math.abs(delta);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestDelta = delta;
    }
  }

  if (bestDelta === null) {
    return null;
  }

  return { delta: bestDelta, distance: bestDistance };
};

type AlignmentSnapOptions = {
  overrides?: { start: THREE.Vector2; end: THREE.Vector2 };
  previous?: AlignmentSnapState;
};

export const calculateAlignmentSnap = (
  object: WallType | SingleLineType | RulerLineType,
  snappingEnd: number,
  edge: THREE.Vector2,
  options: AlignmentSnapOptions = {},
): AlignmentSnapResult => {
  const currentStart = options.overrides?.start ? options.overrides.start.clone() : object.start.clone();
  const currentEnd = options.overrides?.end ? options.overrides.end.clone() : object.end.clone();
  const originalVector = currentEnd.clone().sub(currentStart);

  const start = currentStart.clone();
  const end = currentEnd.clone();

  let snappedX = false;
  let snappedY = false;
  let targetX: number | undefined;
  let targetY: number | undefined;

  const previous = options.previous;
  const editorStore = getEditorStore();
  if (!editorStore) {
    return { snapped: false, snappedX: false, snappedY: false, start: currentStart.clone(), end: currentEnd.clone() };
  }

  // Defensive check: disable all alignment snapping when SHIFT is pressed
  if (editorStore.isShiftPressed) {
    return { snapped: false, snappedX: false, snappedY: false, start: currentStart.clone(), end: currentEnd.clone() };
  }

  const axisApplyThreshold = getInteractionSnappingTolerance(editorStore);
  // Release threshold must be LARGER than apply threshold for proper hysteresis
  // This prevents toggling when near alignment lines
  // Using 1.25x provides good hysteresis without being too sticky
  const axisReleaseThreshold = axisApplyThreshold * 1.25;

  // For edge-mode alignment lines with walls, we need to check edge points, not just center
  // Collect candidates using the appropriate point(s) based on alignment line mode
  const candidates = collectAlignmentCandidates(edge, object, start, end, snappingEnd);
  const { x: selectedX, y: selectedY } = chooseAlignmentTargets(edge, candidates, {
    previous,
    applyThreshold: axisApplyThreshold,
    releaseThreshold: axisReleaseThreshold,
  });

  const applyAxisSnap = (
    axis: "x" | "y",
    selected: AlignmentSnapCandidate | undefined,
    movingPoint: THREE.Vector2,
  ): boolean => {
    if (!selected) {
      return false;
    }

    // Always use the full apply tolerance for the actual snap so single-axis snaps
    // don't get a smaller window after the first lock-in (e.g., when perpendicular).
    const threshold = axisApplyThreshold;
    const lineMode = selected.line.mode === "edge" ? "edge" : "center";

    let delta: number | null = null;
    let distance = Infinity;

    if (lineMode === "edge" && isWallType(object)) {
      const edgeResult = getEdgeAlignmentDelta(object, start, end, axis, selected.coordinate, snappingEnd);
      if (!edgeResult) {
        return false;
      }
      delta = edgeResult.delta;
      distance = edgeResult.distance;
    } else {
      const currentCoord = axis === "x" ? movingPoint.x : movingPoint.y;
      delta = selected.coordinate - currentCoord;
      distance = Math.abs(delta);
    }

    if (Math.abs(distance) <= threshold) {
      if (axis === "x") {
        movingPoint.x += delta;
        if (lineMode !== "edge") {
          movingPoint.x = selected.coordinate;
        }
      } else {
        movingPoint.y += delta;
        if (lineMode !== "edge") {
          movingPoint.y = selected.coordinate;
        }
      }

      if (axis === "x") {
        snappedX = true;
        targetX = selected.coordinate;
      } else {
        snappedY = true;
        targetY = selected.coordinate;
      }

      return true;
    }

    return false;
  };

  if (snappingEnd === WallConnectionStart) {
    const updateEndFromStart = () => {
      end.copy(start).add(originalVector);
    };

    const appliedX = applyAxisSnap("x", selectedX, start);
    if (appliedX) {
      updateEndFromStart();
    }

    const appliedY = applyAxisSnap("y", selectedY, start);
    if (appliedY) {
      updateEndFromStart();
    }

    if (!appliedX && !appliedY) {
      updateEndFromStart();
    }
  } else {
    const updateStartFromEnd = () => {
      start.copy(end).sub(originalVector);
    };

    const appliedX = applyAxisSnap("x", selectedX, end);
    if (appliedX) {
      updateStartFromEnd();
    }

    const appliedY = applyAxisSnap("y", selectedY, end);
    if (appliedY) {
      updateStartFromEnd();
    }

    if (!appliedX && !appliedY) {
      updateStartFromEnd();
    }
  }

  const snapped = snappedX || snappedY;

  return {
    snapped,
    snappedX,
    snappedY,
    start,
    end,
    targetX,
    targetY,
  };
};

// Read-only snapping function that returns targets/positions without mutating state
export const getSnapCandidates = (
  sourceObject: string,
  snappingEnd: number,
  edge: THREE.Vector2,
): Array<{
  snapped: boolean;
  position?: THREE.Vector2;
  targetId?: string;
  targetObject?: any;
  connectionPos?: number;
}> => {
  const editorStore = getEditorStore();
  if (!editorStore || editorStore.isShiftPressed) {
    return [];
  }

  const object = floorplannerStore.findObjectId(sourceObject);
  if (!object) return [];

  const candidates = [];

  // Check walls with spatial optimization
  const wallsToCheck = floorplannerStore.enableSpatialOptimization
    ? getNearbyWalls(edge, 2.0)
    : Array.from(floorplannerStore.wallsMap.values());

  for (const targetWall of wallsToCheck) {
    const candidate = checkAgainstObjectReadOnly(
      object.id,
      targetWall.id,
      snappingEnd,
      edge,
    );
    if (candidate) {
      candidates.push(candidate);
    }
  }

  return candidates;
};

const checkAgainstObjectReadOnly = (
  source: string,
  target: string,
  snappingEnd: number,
  edge: THREE.Vector2,
): any => {
  const sourceObject = floorplannerStore.findObjectId(source);
  const targetObject = floorplannerStore.findObjectId(target);
  if (sourceObject && targetObject) {
    // Check if already connected
    const alreadyConnected = targetObject.centerConnections?.find(
      (c: { id: string; }) => c.id === sourceObject.id,
    );
    if (!alreadyConnected && targetObject.id !== sourceObject.id) {
      if (!targetObject.start || !targetObject.end) {
        return null;
      }

      // Get editorStore before using it for tolerance checks
      const editorStore = getEditorStore();
      if (!editorStore) return null;

      // Use segment distance check
      // Use a search radius derived from tolerance (considerably larger than tolerance for broader search)
      // but not hard-coded - derive from tolerance for consistency
      const snappingTolerance = getInteractionSnappingTolerance(editorStore);
      const searchRadius = snappingTolerance * 30; // 30x tolerance for broader search
      const seg = new THREE.Line3(new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0), new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0));
      const cp = new THREE.Vector3();
      seg.closestPointToPoint(new THREE.Vector3(edge.x, edge.y, 0), true, cp);
      const dist = edge.distanceTo(new THREE.Vector2(cp.x, cp.y));
      if (dist > searchRadius) return null;

      // Calculate closest point
      const targetLine = new THREE.Line3(
        new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0),
        new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0),
      );
      const closestEdge = new THREE.Vector3();
      targetLine.closestPointToPoint(
        new THREE.Vector3(edge.x, edge.y, 0),
        true,
        closestEdge,
      );

      // editorStore already retrieved above

      // Check if within snap threshold
      if (edge.distanceTo(new THREE.Vector2(closestEdge.x, closestEdge.y)) < snappingTolerance) {
        // Snap to endpoints if within tolerance
        if (closestEdge.distanceTo(new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0)) < snappingTolerance) {
          closestEdge.set(targetObject.start.x, targetObject.start.y, 0);
        } else if (closestEdge.distanceTo(new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0)) < snappingTolerance) {
          closestEdge.set(targetObject.end.x, targetObject.end.y, 0);
        }

        const snappedPosition = new THREE.Vector2(closestEdge.x, closestEdge.y);

        // Calculate connection position
        let connectionPos = targetObject.start.distanceTo(snappedPosition);
        if (editorStore && connectionPos + snappingTolerance >= targetObject.start.distanceTo(targetObject.end)) {
          connectionPos = WallConnectionEnd;
        }
        if (editorStore && connectionPos - snappingTolerance <= 0) {
          connectionPos = WallConnectionStart;
        }

        return {
          snapped: true,
          position: snappedPosition,
          targetId: targetObject.id,
          targetObject: targetObject,
          connectionPos: connectionPos,
          snappingEnd: snappingEnd
        };
      }
    }
  }
  return null;
};

const shouldAllowEdgeSnap = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end" | "middle",
): boolean => {
  // Get wall lengths for validation
  const sourceWallLength = sourceWall.start.distanceTo(sourceWall.end);
  const targetWallLength = targetWall.start.distanceTo(targetWall.end);

  // Get positions of the endpoints being connected
  const sourcePos = sourceEndpoint === "start" ? sourceWall.start : sourceWall.end;

  // For target position, handle mid-point case
  let targetPos: THREE.Vector2;
  if (targetEndpoint === "start") {
    targetPos = targetWall.start.clone();
  } else if (targetEndpoint === "end") {
    targetPos = targetWall.end.clone();
  } else {
    // "middle" - use the midpoint of the target wall
    targetPos = new THREE.Vector2(
      (targetWall.start.x + targetWall.end.x) / 2,
      (targetWall.start.y + targetWall.end.y) / 2
    );
  }

  const debugPayload = {
    sourceWallId: sourceWall.id,
    targetWallId: targetWall.id,
    sourceEndpoint,
    targetEndpoint,
  };

  // For endpoints, we need to check the opposite endpoint for overlap validation
  // For mid-point connections, we skip this check (no wrapping issue with mid-point)
  if (targetEndpoint !== "middle") {
    const targetOtherPos = targetEndpoint === "start" ? targetWall.end : targetWall.start;

    // VALIDATION 1: Prevent far-end snap
    // Ensure we're snapping to the closer endpoint, not the far one
    // This prevents snapping to the wrong side of the target wall
    const targetEndDist = sourcePos.distanceTo(targetPos);
    const targetStartDist = sourcePos.distanceTo(targetOtherPos);
    if (targetEndDist > targetStartDist) {
      // Rejecting snap: far-end check - snapping to wrong side of target wall
      return false;
    }
  }

  // VALIDATION 2: Check existing source endpoint connections
  // Each endpoint can only have one connection
  // This prevents multiple connections to the same endpoint
  if (sourceWall.edgeConnections) {
    const existingConnection = sourceWall.edgeConnections.find(
      (conn) => conn.sourceEndpoint === sourceEndpoint
    );
    if (existingConnection) {
      // Rejecting snap: source endpoint already connected
      return false;
    }
  }

  // VALIDATION 3: Multi-joint support
  // Previously, this blocked endpoint-to-endpoint connections if target endpoint was already connected.
  // This has been REMOVED to allow multi-wall joints (3+ walls connecting at the same endpoint).
  // 
  // Multi-joints occur when:
  // - Multiple walls connect to the same target endpoint (start or end)
  // - Each wall may connect to a different corner of that endpoint
  // - The JointDetector will recognize this as a multi-joint and generate appropriate polygons
  //
  // For mid-point connections (T-junctions), multiple walls can connect to different positions
  // along the target wall's middle, which is handled by separate connection tracking.

  // All validations passed
  return true;
};

type ForceSideOptions = "left" | "right" | "center" | {
  sourceSide?: "left" | "right" | "center";
  targetSide?: "left" | "right" | "center";
};

const EDGE_SIDE_PAIR_OPTIONS: Array<{
  sourceSide: "left" | "right";
  targetSide: "left" | "right";
}> = [
    { sourceSide: "left", targetSide: "left" },
    { sourceSide: "left", targetSide: "right" },
    { sourceSide: "right", targetSide: "left" },
    { sourceSide: "right", targetSide: "right" },
  ];

const getSourceEdgeReferencePosition = (
  wall: WallType,
  endpoint: "start" | "end",
  forceSide?: ForceSideOptions,
): THREE.Vector2 => {
  const sourceSide = typeof forceSide === "string"
    ? forceSide
    : forceSide?.sourceSide;

  if (!sourceSide || sourceSide === "center") {
    return getEndpointPosition(wall, endpoint).clone();
  }

  return calculateWallEdgePoint(
    wall,
    endpoint,
    0,
    undefined,
    undefined,
    sourceSide,
  ).edgePosition.clone();
};

const compareEdgeSnapPreference = (
  edgePosition: THREE.Vector2,
  a: { snappingPosition: THREE.Vector2; edgeConnection: WallEdgeConnection },
  b: { snappingPosition: THREE.Vector2; edgeConnection: WallEdgeConnection },
): number => {
  const shiftA = a.snappingPosition.distanceTo(edgePosition);
  const shiftB = b.snappingPosition.distanceTo(edgePosition);
  if (Math.abs(shiftA - shiftB) > 1e-6) {
    return shiftA - shiftB;
  }

  const sourceA = toVector2(a.edgeConnection.sourceEdgePoint).distanceTo(edgePosition);
  const sourceB = toVector2(b.edgeConnection.sourceEdgePoint).distanceTo(edgePosition);
  const targetA = toVector2(a.edgeConnection.targetEdgePoint).distanceTo(edgePosition);
  const targetB = toVector2(b.edgeConnection.targetEdgePoint).distanceTo(edgePosition);
  const nearestCornerA = Math.min(sourceA, targetA);
  const nearestCornerB = Math.min(sourceB, targetB);
  if (Math.abs(nearestCornerA - nearestCornerB) > 1e-6) {
    return nearestCornerA - nearestCornerB;
  }

  return (sourceA + targetA) - (sourceB + targetB);
};

const signedPolygonArea = (points: THREE.Vector2[]): number => {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const next = points[(i + 1) % points.length];
    area += points[i].x * next.y - next.x * points[i].y;
  }
  return area / 2;
};

const lineIntersection = (
  a: THREE.Vector2,
  b: THREE.Vector2,
  c: THREE.Vector2,
  d: THREE.Vector2,
): THREE.Vector2 | null => {
  const ab = b.clone().sub(a);
  const cd = d.clone().sub(c);
  const denominator = ab.x * cd.y - ab.y * cd.x;
  if (Math.abs(denominator) < 1e-9) {
    return null;
  }

  const ac = c.clone().sub(a);
  const t = (ac.x * cd.y - ac.y * cd.x) / denominator;
  return a.clone().addScaledVector(ab, t);
};

const clipPolygonToConvexPolygon = (
  subject: THREE.Vector2[],
  clip: THREE.Vector2[],
): THREE.Vector2[] => {
  if (subject.length < 3 || clip.length < 3) {
    return [];
  }

  let output = subject.map(point => point.clone());
  const clipWinding = signedPolygonArea(clip) >= 0 ? 1 : -1;
  const epsilon = 1e-8;

  for (let i = 0; i < clip.length; i++) {
    const clipA = clip[i];
    const clipB = clip[(i + 1) % clip.length];
    const input = output;
    output = [];

    if (input.length === 0) {
      break;
    }

    const isInside = (point: THREE.Vector2): boolean => {
      const edge = clipB.clone().sub(clipA);
      const toPoint = point.clone().sub(clipA);
      const cross = edge.x * toPoint.y - edge.y * toPoint.x;
      return clipWinding > 0 ? cross >= -epsilon : cross <= epsilon;
    };

    for (let j = 0; j < input.length; j++) {
      const current = input[j];
      const previous = input[(j + input.length - 1) % input.length];
      const currentInside = isInside(current);
      const previousInside = isInside(previous);

      if (currentInside) {
        if (!previousInside) {
          const intersection = lineIntersection(previous, current, clipA, clipB);
          if (intersection) {
            output.push(intersection);
          }
        }
        output.push(current);
      } else if (previousInside) {
        const intersection = lineIntersection(previous, current, clipA, clipB);
        if (intersection) {
          output.push(intersection);
        }
      }
    }
  }

  return output;
};

const getStraightWallOverlapArea = (
  wallA: WallType,
  wallB: WallType,
): number => {
  if (wallA.lineForm === "arc" || wallB.lineForm === "arc") {
    return 0;
  }

  const polygonA = getWallPolygon(wallA);
  const polygonB = getWallPolygon(wallB);
  if (!polygonA || !polygonB) {
    return 0;
  }

  const intersection = clipPolygonToConvexPolygon(polygonA, polygonB);
  return Math.abs(signedPolygonArea(intersection));
};

const getEdgeSnapCandidateOverlapArea = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  candidate: {
    snapResult: {
      snappingPosition: THREE.Vector2;
    };
  },
  candidateWalls: WallType[],
): number => {
  const sourceAtCandidate = cloneWallWithEndpoint(
    sourceWall,
    sourceEndpoint,
    candidate.snapResult.snappingPosition,
  );
  return candidateWalls.reduce((total, targetWall) => {
    if (targetWall.id === sourceWall.id) {
      return total;
    }
    return total + getStraightWallOverlapArea(sourceAtCandidate, targetWall);
  }, 0);
};

export const compareEdgeSnapCandidatePreferenceWithOverlap = (
  sourceWall: WallType,
  sourceEndpoint: "start" | "end",
  candidateWalls: WallType[],
  edgePosition: THREE.Vector2,
  a: {
    snapResult: {
      snappingPosition: THREE.Vector2;
      edgeConnection: WallEdgeConnection;
    };
  },
  b: {
    snapResult: {
      snappingPosition: THREE.Vector2;
      edgeConnection: WallEdgeConnection;
    };
  },
): number => {
  const overlapA = getEdgeSnapCandidateOverlapArea(
    sourceWall,
    sourceEndpoint,
    a,
    candidateWalls,
  );
  const overlapB = getEdgeSnapCandidateOverlapArea(
    sourceWall,
    sourceEndpoint,
    b,
    candidateWalls,
  );
  if (Math.abs(overlapA - overlapB) > 1e-8) {
    return overlapA - overlapB;
  }

  return compareEdgeSnapPreference(edgePosition, a.snapResult, b.snapResult);
};

const getDirectionalConnectionKey = (sourceWallId: string, targetWallId: string): string => {
  return `${sourceWallId}->${targetWallId}`;
};

const collectDirectionalConnectionKeys = (wallIds: Iterable<string>): Set<string> => {
  const keys = new Set<string>();

  for (const wallId of wallIds) {
    const wall = floorplannerStore.wallsMap.get(wallId);
    wall?.edgeConnections?.forEach((connection) => {
      keys.add(getDirectionalConnectionKey(wallId, connection.targetWallId));
    });
  }

  return keys;
};

const rememberCommittedEdgeConnection = (
  keys: Set<string>,
  connection: WallEdgeConnection,
): void => {
  keys.add(getDirectionalConnectionKey(connection.sourceWallId, connection.targetWallId));

  if (connection.targetEndpoint === "start" || connection.targetEndpoint === "end") {
    keys.add(getDirectionalConnectionKey(connection.targetWallId, connection.sourceWallId));
  }
};

const resolveMultiJointEndpointForWall = (
  wall: WallType,
  referencePoints: THREE.Vector2[],
  tolerance: number,
): WallEndpoint | null => {
  let bestEndpoint: WallEndpoint | null = null;
  let bestDistance = Infinity;

  const considerEndpoint = (endpoint: WallEndpoint, distance: number) => {
    if (distance > tolerance) {
      return;
    }
    if (distance < bestDistance) {
      bestEndpoint = endpoint;
      bestDistance = distance;
    }
  };

  for (const referencePoint of referencePoints) {
    considerEndpoint("start", wall.start.distanceTo(referencePoint));
    considerEndpoint("end", wall.end.distanceTo(referencePoint));

    const effectiveSnap = findEffectiveEndpointSnap(wall, referencePoint, tolerance);
    if (effectiveSnap) {
      considerEndpoint(effectiveSnap.endpoint, effectiveSnap.distance);
    }
  }

  return bestEndpoint;
};

const findSupplementalMultiJointConnection = (
  sourceWall: WallType,
  sourceEndpoint: WallEndpoint,
  targetWallId: string,
): WallEdgeConnection | null => {
  const edgePosition = getEndpointPosition(sourceWall, sourceEndpoint).clone();
  const sourceWallForSnap: WallType = {
    ...sourceWall,
    start: sourceWall.start.clone(),
    end: sourceWall.end.clone(),
    controlPoint: sourceWall.controlPoint?.clone(),
    edgeConnections: [],
  };

  let bestResult: {
    snappingPosition: THREE.Vector2;
    edgeConnection: WallEdgeConnection;
  } | null = null;

  for (const sidePair of EDGE_SIDE_PAIR_OPTIONS) {
    const sourceReferencePosition = getSourceEdgeReferencePosition(sourceWallForSnap, sourceEndpoint, sidePair);
    const snapResult = snapToWallEdge(
      sourceWall.id,
      sourceEndpoint,
      edgePosition,
      targetWallId,
      sidePair,
      sourceWallForSnap,
      sourceReferencePosition,
    );

    if (snapResult.snapped && snapResult.edgeConnection && snapResult.snappingPosition) {
      const candidateResult = {
        snappingPosition: snapResult.snappingPosition,
        edgeConnection: snapResult.edgeConnection,
      };

      if (!bestResult || compareEdgeSnapPreference(edgePosition, candidateResult, bestResult) < 0) {
        bestResult = candidateResult;
      }
    }
  }

  return bestResult?.edgeConnection ?? null;
};

const snapToWallEdge = (
  sourceWallId: string,
  sourceEndpoint: "start" | "end",
  edgePosition: THREE.Vector2,
  targetWallId: string,
  forceSide?: ForceSideOptions,
  sourceWallOverride?: WallType,
  sourceReferencePosition?: THREE.Vector2,
): {
  snapped: boolean;
  edgeConnection?: WallEdgeConnection;
  targetEndpoint?: "start" | "end" | "middle";
  snappingPosition?: THREE.Vector2;
} => {
  if (sourceWallId === targetWallId) {
    return { snapped: false };
  }

  const sourceWall = sourceWallOverride ?? floorplannerStore.wallsMap.get(sourceWallId);
  const targetWall = floorplannerStore.wallsMap.get(targetWallId);
  if (!sourceWall || !targetWall) {
    return { snapped: false };
  }

  const referencePosition = sourceReferencePosition?.clone() ?? edgePosition.clone();
  const editorStore = getEditorStore();
  const snappingTolerance = getInteractionSnappingTolerance(editorStore);
  const maxIterations = 3;
  const maxSnapShift = snappingTolerance * 2; // prevent large jumps

  // Find the closest position (start, end, or middle) on the target wall
  // For T-junctions ("middle"), this also returns the projected point on the wall's centerline
  const closestTargetPosition = getClosestPosition(referencePosition, targetWall);
  // For backwards compatibility, we'll use "middle" as a position, not an endpoint
  // But in the validation, we need to treat it specially since it's not an actual endpoint
  let targetEndpoint: "start" | "end" | "middle" = closestTargetPosition.position;
  let targetProjectedPoint = closestTargetPosition.projectedPoint?.clone();
  const targetWidth = targetWall.wallWidth ?? floorplannerStore.wallWidth;
  const sourceWidth = sourceWall.wallWidth ?? floorplannerStore.wallWidth;
  const initialSourceWall = cloneWallWithEndpoint(sourceWall, sourceEndpoint, edgePosition);
  const cornerTolerance = computeCornerTolerance(sourceWall, targetWall);
  const findWideEndCapMiddleEndpoint = (): WallEndpoint | null => {
    if (
      !areWallsNearlyCollinear(initialSourceWall, targetWall) ||
      (targetWidth - sourceWidth) / 2 <= snappingTolerance + 1e-6
    ) {
      return null;
    }

    const sourceCorners = getEndpointCorners(initialSourceWall, sourceEndpoint);
    const targetCorners = buildWallEndpointCorners(targetWall);
    if (!sourceCorners || !targetCorners) {
      return null;
    }

    let bestEndpoint: WallEndpoint | null = null;
    let bestDistance = Infinity;
    for (const endpoint of ["start", "end"] as WallEndpoint[]) {
      const capCorners = endpoint === "start" ? targetCorners.startCorners : targetCorners.endCorners;
      const maxCornerDistance = Math.max(
        distanceToSegment(sourceCorners[0], capCorners[0], capCorners[1]),
        distanceToSegment(sourceCorners[1], capCorners[0], capCorners[1]),
      );
      if (maxCornerDistance > snappingTolerance + 1e-6) {
        continue;
      }

      const sourceEndpointPosition = getEndpointPosition(initialSourceWall, sourceEndpoint);
      const centerDistance = distanceToSegment(sourceEndpointPosition, capCorners[0], capCorners[1]);
      if (centerDistance < bestDistance) {
        bestEndpoint = endpoint;
        bestDistance = centerDistance;
      }
    }

    return bestEndpoint;
  };
  const wideEndCapMiddleEndpoint = findWideEndCapMiddleEndpoint();
  if (wideEndCapMiddleEndpoint) {
    targetEndpoint = "middle";
    targetProjectedPoint = getEndpointPosition(targetWall, wideEndCapMiddleEndpoint).clone();
  }
  const effectiveEndpointSnap = findEffectiveEndpointSnap(
    targetWall,
    referencePosition,
    snappingTolerance + Math.max(sourceWidth, targetWidth),
  );

  if (effectiveEndpointSnap && !wideEndCapMiddleEndpoint) {
    const actualReferenceDistance =
      targetEndpoint === "start"
        ? referencePosition.distanceTo(targetWall.start)
        : targetEndpoint === "end"
          ? referencePosition.distanceTo(targetWall.end)
          : targetProjectedPoint
            ? referencePosition.distanceTo(targetProjectedPoint)
            : Infinity;

    if (targetEndpoint === "middle" || effectiveEndpointSnap.distance <= actualReferenceDistance + 1e-6) {
      targetEndpoint = effectiveEndpointSnap.endpoint;
    }
  }

  // If closest position is middle but we're actually near an endpoint, resolve to endpoint
  if (targetEndpoint === "middle") {
    const edgeDistStart = referencePosition.distanceTo(targetWall.start);
    const edgeDistEnd = referencePosition.distanceTo(targetWall.end);
    const sourceDistStart = edgeDistStart;
    const sourceDistEnd = edgeDistEnd;
    const endpointResolutionThreshold = Math.max(snappingTolerance * 1.1, sourceWidth * 0.75 + snappingTolerance * 0.25);

    const nearStart = edgeDistStart < endpointResolutionThreshold && sourceDistStart < endpointResolutionThreshold;
    const nearEnd = edgeDistEnd < endpointResolutionThreshold && sourceDistEnd < endpointResolutionThreshold;
    const dragging = renderStore.isDragging;

    if (!dragging && !wideEndCapMiddleEndpoint && (nearStart || nearEnd)) {
      const chooserStart = Math.min(sourceDistStart, edgeDistStart);
      const chooserEnd = Math.min(sourceDistEnd, edgeDistEnd);
      const newEndpoint = chooserStart <= chooserEnd ? "start" : "end";
      targetEndpoint = newEndpoint;
    }
  }

  // CRITICAL: Check embedding BEFORE any snap iterations
  // This validates the original drop position, ensuring both corners are already
  // within the target wall BEFORE we start moving the wall to align perfectly.
  // This prevents "Vertex-to-Vertex" false positives where the snap moves one
  // corner onto the target's corner, making it pass the check after the fact.
  const preSnapEmbedding = connectionEmbeddingPass(
    initialSourceWall,
    sourceEndpoint,
    targetWall,
    targetEndpoint,
    cornerTolerance
  );
  const effectivePreSnapEmbedding =
    !preSnapEmbedding.pass && effectiveEndpointSnap && effectiveEndpointSnap.endpoint === targetEndpoint
      ? connectionEmbeddingPassWithEffectiveTarget(initialSourceWall, sourceEndpoint, targetWall, effectiveEndpointSnap, cornerTolerance)
      : null;
  const effectiveEndpointTouch =
    !preSnapEmbedding.pass && !effectivePreSnapEmbedding?.pass && effectiveEndpointSnap && effectiveEndpointSnap.endpoint === targetEndpoint
      ? hasEffectiveEndpointTouch(initialSourceWall, sourceEndpoint, targetWall, effectiveEndpointSnap, Math.max(cornerTolerance, snappingTolerance * 0.5))
      : false;
  let voidFillMetadata: VoidFillMetadata | null = null;
  if (!preSnapEmbedding.pass && !effectivePreSnapEmbedding?.pass && !effectiveEndpointTouch) {
    const debugLog = editorStore?.isDebugModeActive || editorStore?.debugJointPolygons;
    voidFillMetadata = detectSingleCornerTouchConnection(
      initialSourceWall,
      sourceEndpoint,
      targetWall,
      targetEndpoint,
      cornerTolerance,
      snappingTolerance
    );
    if (voidFillMetadata?.resolvedTargetEndpoint) {
      targetEndpoint = voidFillMetadata.resolvedTargetEndpoint;
    }
    if (!voidFillMetadata) {
      return { snapped: false };
    }
  }

  let snappingPosition = edgePosition.clone();
  let finalConnection: WallEdgeConnection | null = null;
  let finalSourceEdge: THREE.Vector2 | null = null;
  let finalTargetEdge: THREE.Vector2 | null = null;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const workingSourceWall = cloneWallWithEndpoint(sourceWall, sourceEndpoint, snappingPosition);

    // Run validation on the current working geometry
    if (!shouldAllowEdgeSnap(workingSourceWall, sourceEndpoint, targetWall, targetEndpoint)) {
      return { snapped: false };
    }

    const connection = applyEffectiveEndpointSnapToConnection(
      createEdgeConnection(
        workingSourceWall,
        sourceEndpoint,
        targetWall,
        targetEndpoint,
        targetProjectedPoint, // Pass projected point for T-junctions
        forceSide, // Pass forceSide to control which side to connect to
      ),
      effectiveEndpointSnap,
    );

    const sourceEdgePoint = toVector2(connection.sourceEdgePoint);
    const targetEdgePoint = toVector2(connection.targetEdgePoint);
    const gapVector = new THREE.Vector2().subVectors(targetEdgePoint, sourceEdgePoint);
    const gapDistance = gapVector.length();

    // Add small epsilon to handle floating-point precision at boundary cases
    // Without this, gapDistance=0.100 with tolerance=0.100 can be rejected due to precision
    const epsilon = 0.001;
    if (iteration === 0 && gapDistance > snappingTolerance + epsilon) {
      return { snapped: false };
    }

    finalConnection = connection;
    finalSourceEdge = sourceEdgePoint;
    finalTargetEdge = targetEdgePoint;
    if (voidFillMetadata) {
      finalConnection.voidFill = voidFillMetadata;
    }

    if (gapDistance < 1e-4) {
      break;
    }

    snappingPosition.add(gapVector);
  }

  if (!finalConnection || !finalSourceEdge || !finalTargetEdge) {
    return { snapped: false };
  }

  const finalGap = finalSourceEdge.distanceTo(finalTargetEdge);
  if (finalGap > snappingTolerance) {
    return { snapped: false };
  }

  const snapShift = snappingPosition.distanceTo(edgePosition);
  if (snapShift > maxSnapShift) {
    return { snapped: false };
  }

  const endpointSnapTolerance = snappingTolerance * 1.5;
  if (targetEndpoint === "middle" && !wideEndCapMiddleEndpoint) {
    const distToStart = finalTargetEdge.distanceTo(targetWall.start);
    const distToEnd = finalTargetEdge.distanceTo(targetWall.end);
    if (distToStart < endpointSnapTolerance || distToEnd < endpointSnapTolerance) {
      targetEndpoint = distToStart <= distToEnd ? "start" : "end";
      const finalSourceWall = cloneWallWithEndpoint(sourceWall, sourceEndpoint, snappingPosition);
      const recomputedConnection = applyEffectiveEndpointSnapToConnection(
        createEdgeConnection(
          finalSourceWall,
          sourceEndpoint,
          targetWall,
          targetEndpoint,
          undefined,
          forceSide,
        ),
        effectiveEndpointSnap,
      );
      if (voidFillMetadata) {
        recomputedConnection.voidFill = voidFillMetadata;
      }
      finalConnection = recomputedConnection;
    }
  }


  return {
    snapped: true,
    edgeConnection: finalConnection,
    targetEndpoint,
    snappingPosition: snappingPosition.clone(),
  };
};

const getEdgeSnapCandidates = (
  sourceWallId: string,
  sourceEndpoint: "start" | "end",
  edgePosition: THREE.Vector2,
  sourceWallOverride?: WallType,
): Array<{
  snapped: boolean;
  edgeConnection: WallEdgeConnection;
  targetEndpoint: "start" | "end" | "middle";
  snappingPosition: THREE.Vector2;
}> => {
  const editor = getEditorStore();
  const snappingTolerance = getInteractionSnappingTolerance(editor);
  const sourceWall = sourceWallOverride ?? floorplannerStore.wallsMap.get(sourceWallId);
  if (!sourceWall) {
    return [];
  }
  const defaultWidth = floorplannerStore.wallWidth || 0;
  const sourceHalfWidth = (sourceWall.wallWidth ?? defaultWidth) / 2;

  const candidates: Array<{
    snapped: boolean;
    edgeConnection: WallEdgeConnection;
    targetEndpoint: "start" | "end" | "middle";
    snappingPosition: THREE.Vector2;
  }> = [];

  // Get nearby walls
  const wallsToCheck = floorplannerStore.enableSpatialOptimization
    ? getNearbyWalls(edgePosition, 2.0)
    : Array.from(floorplannerStore.wallsMap.values());

  for (const targetWall of wallsToCheck) {
    // Skip self-connections
    if (targetWall.id === sourceWallId) {
      continue;
    }

    // Try every visible corner pairing between the dragged wall endpoint and the target wall.
    // Curved walls frequently need opposite left/right labels on the two walls.
    for (const sidePair of EDGE_SIDE_PAIR_OPTIONS) {
      const sourceReferencePosition = getSourceEdgeReferencePosition(sourceWall, sourceEndpoint, sidePair);
      const { distance } = getClosestPosition(sourceReferencePosition, targetWall);

      // Skip if beyond snapping tolerance
      const targetHalfWidth = (targetWall.wallWidth ?? defaultWidth) / 2;
      const distanceAllowance = snappingTolerance + sourceHalfWidth + targetHalfWidth;
      if (distance > distanceAllowance) {
        const effectiveEndpointCandidate = findEffectiveEndpointSnap(
          targetWall,
          sourceReferencePosition,
          distanceAllowance + Math.max(sourceHalfWidth, targetHalfWidth),
        );
        if (!effectiveEndpointCandidate) {
          continue;
        }
      }

      const snapResult = snapToWallEdge(
        sourceWallId,
        sourceEndpoint,
        edgePosition,
        targetWall.id,
        sidePair,
        sourceWall,
        sourceReferencePosition,
      );

      if (snapResult.snapped && snapResult.edgeConnection && snapResult.snappingPosition) {
        candidates.push({
          snapped: true,
          edgeConnection: snapResult.edgeConnection,
          targetEndpoint: snapResult.targetEndpoint!,
          snappingPosition: snapResult.snappingPosition.clone(),
        });
      }
    }
  }

  return candidates;
};

export const snapToOtherObjects = (
  sourceObject: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
  options: { allowDuringDeferred?: boolean; forceCenterMode?: boolean } = {},
): boolean | SnappingResult => {
  const editorStore = getEditorStore();
  if (editorStore?.isShiftPressed) {
    return false;
  }

  const object = floorplannerStore.findObjectId(sourceObject);
  if (!object) {
    return false;
  }

  // Make sure that the snappingEnd is free from any connections
  const isStartEndpoint = snappingEnd === WallConnectionStart;
  if (isWallType(object)) {
    const endpointKey = isStartEndpoint ? "start" : "end";
    const existingEdgeConnection = object.edgeConnections?.some(
      (connection) => connection.sourceEndpoint === endpointKey
    );
    if (existingEdgeConnection) {
      return false;
    }
  }

  if (object.centerConnections) {
    const blockingConnection = object.centerConnections.find(
      (c) => c.sourcePosition === snappingEnd && c.mode !== "edge",
    );
    if (blockingConnection) {
      return false;
    }
  }

  // Intelligent snapping: determine if we should use edge or center connections
  //
  // Decision point:
  // - When ALT/OPTION is NOT pressed (default): use ONLY edge-based snapping (addEdgeConnection)
  // - When ALT/OPTION is pressed: use center-based snapping (addConnection)
  // - Endpoint snaps (start/end) → use edge-based snapping (addEdgeConnection)
  // - Mid-point snaps → use center-based snapping (addConnection)
  //
  // For walls, try edge-based snapping first (for endpoint connections)
  // Edge snapping is the default unless ALT/OPTION key is pressed
  const shouldUseEdgeSnapping =
    object.type === "wall" && !editorStore?.isAltPressed && !options.forceCenterMode;
  if (shouldUseEdgeSnapping) {
    const edgeSnapResult = snapToOtherObjectsEdgeBased(sourceObject, snappingEnd, edge, updateOtherEnd, options);
    if (edgeSnapResult) {
      // Endpoint snap successful - will result in addEdgeConnection()
      return edgeSnapResult;
    }
    // IMPORTANT: Do NOT fall back to center snapping when in edge mode
    // If edge snapping didn't find a match, return no snap (not center snap)
    return { snapped: false };
  }

  // Center-based snapping (only when ALT is pressed or for non-wall objects):
  // - Mid-point connections (when snapping to wall middle)
  // - SingleLine and RulerLine targets
  // - Explicit center snap mode when ALT/OPTION key is held
  // This will result in addConnection() for mid-point connections
  const wallsToCheck = floorplannerStore.enableSpatialOptimization
    ? getNearbyWalls(edge, 2.0)
    : Array.from(floorplannerStore.wallsMap.values());

  for (const targetWall of wallsToCheck) {
    const snappedResult = checkAgainstObject(
      object.id,
      targetWall.id,
      snappingEnd,
      edge,
      updateOtherEnd,
      options,
    );
    if (snappedResult) {
      return snappedResult;
    }
  }

  for (const targetLine of floorplannerStore.singleLinesMap.values()) {
    const snappedResult = checkAgainstObject(
      object.id,
      targetLine.id,
      snappingEnd,
      edge,
      updateOtherEnd,
      options,
    );
    if (snappedResult) {
      return snappedResult;
    }
  }

  for (const targetRuler of floorplannerStore.rulerLinesMap.values()) {
    const snappedResult = checkAgainstObject(
      object.id,
      targetRuler.id,
      snappingEnd,
      edge,
      updateOtherEnd,
      options,
    );
    if (snappedResult) {
      return snappedResult;
    }
  }

  return false;
};

export const snapToOtherObjectsEdgeBased = (
  sourceObject: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
  options: { allowDuringDeferred?: boolean } = {},
): boolean | SnappingResult => {
  const editor = getEditorStore();
  if (editor?.isShiftPressed) {
    return false;
  }

  const sourceWall = floorplannerStore.findObjectId(sourceObject) as WallType;
  if (!sourceWall) {
    return false;
  }

  const sourceEndpoint = snappingEnd === WallConnectionStart ? "start" : "end";

  // Use spatial optimization to limit snapping calculations to nearby walls
  const wallsToCheck = floorplannerStore.enableSpatialOptimization
    ? getNearbyWalls(edge, 2.0)
    : Array.from(floorplannerStore.wallsMap.values());

  // Collect all valid snap candidates from all walls
  const allCandidates: Array<{
    snapResult: {
      snapped: boolean;
      edgeConnection: WallEdgeConnection;
      targetEndpoint: "start" | "end" | "middle";
      snappingPosition: THREE.Vector2;
    };
    targetWall: WallType;
  }> = [];

  for (const targetWall of wallsToCheck) {
    // Guard against self-connections
    if (targetWall.id === sourceWall.id) {
      continue;
    }

    // Try every visible corner pairing between the source and target walls.
    for (const sidePair of EDGE_SIDE_PAIR_OPTIONS) {
      const sourceReferencePosition = getSourceEdgeReferencePosition(sourceWall, sourceEndpoint, sidePair);
      const snapResult = snapToWallEdge(
        sourceWall.id,
        sourceEndpoint,
        edge,
        targetWall.id,
        sidePair,
        undefined,
        sourceReferencePosition,
      );

      if (snapResult.snapped && snapResult.edgeConnection && snapResult.snappingPosition) {
        allCandidates.push({
          snapResult: {
            snapped: snapResult.snapped,
            edgeConnection: snapResult.edgeConnection,
            targetEndpoint: snapResult.targetEndpoint!,
            snappingPosition: snapResult.snappingPosition,
          },
          targetWall,
        });
      }
    }
  }

  // If no candidates found, return false
  if (allCandidates.length === 0) {
    return false;
  }

  const sourceOverlapWidth = sourceWall.wallWidth ?? floorplannerStore.wallWidth ?? 0.2;
  const overlapSearchRadius = Math.max(
    0.5,
    sourceOverlapWidth * 3 + getInteractionSnappingTolerance(editor),
  );
  const overlapCandidateWalls = getNearbyWalls(edge, overlapSearchRadius);
  if (!overlapCandidateWalls.some(wall => wall.id === sourceWall.id)) {
    overlapCandidateWalls.push(sourceWall);
  }

  // Sort candidates by wall-body overlap first, then distance to the dragged
  // endpoint. Equal-distance side pairs can otherwise place the last-joined
  // wall through the target wall, making the rendered joint order-dependent.
  const sortedCandidates = allCandidates.sort((a, b) =>
    compareEdgeSnapCandidatePreferenceWithOverlap(
      sourceWall,
      sourceEndpoint,
      overlapCandidateWalls,
      edge,
      a,
      b,
    )
  );

  const bestCandidate = sortedCandidates[0];
  const bestTargetEndpoint = bestCandidate.snapResult.targetEndpoint;
  const bestTargetWallId = bestCandidate.targetWall.id;

  // MULTI-JOINT SUPPORT: Find ALL walls that would form a multi-joint at the TARGET endpoint
  // This enables forming multi-joints (3+ walls) when connecting one wall at a time
  // 
  // Key insight: When source wall snaps to target wall's endpoint, we need to find OTHER walls
  // that ALREADY have connections to that same target endpoint. These walls are part of the
  // multi-joint even if their physical endpoints aren't exactly at the snap position.
  const multiJointCandidates: typeof allCandidates = [bestCandidate];

  // Get the source wall's endpoint position (where the multi-joint would form)
  const sourceEndpointPos = bestCandidate.snapResult.snappingPosition;
  const maxWallWidth = Math.max(
    sourceWall.wallWidth ?? floorplannerStore.wallWidth,
    bestCandidate.targetWall.wallWidth ?? floorplannerStore.wallWidth
  );
  const multiJointTolerance = maxWallWidth * 2; // Generous tolerance to catch walls at different corners

  // Track walls already in the multi-joint (for deduplication)
  const multiJointWallIds = new Set<string>([sourceWall.id, bestTargetWallId]);
  const multiJointEndpointByWallId = new Map<string, WallEndpoint>([[sourceWall.id, sourceEndpoint]]);
  const finalSourceWallAtJoint = cloneWallWithEndpoint(sourceWall, sourceEndpoint, sourceEndpointPos);
  const sourceEndpointCornersAtJoint = getEndpointCorners(finalSourceWallAtJoint, sourceEndpoint);
  const sourceJointReferencePoints = [
    sourceEndpointPos,
    ...(sourceEndpointCornersAtJoint ? Array.from(sourceEndpointCornersAtJoint) : []),
  ];

  // Get the target wall's endpoint position for geometric checks
  const targetWall = bestCandidate.targetWall;
  const targetEndpointPos = bestTargetEndpoint === 'start' ? targetWall.start :
    bestTargetEndpoint === 'end' ? targetWall.end :
      sourceEndpointPos; // 'middle' - use snap position
  if (bestTargetEndpoint === "start" || bestTargetEndpoint === "end") {
    multiJointEndpointByWallId.set(bestTargetWallId, bestTargetEndpoint);
  }

  // METHOD 1: Find walls that have EXISTING edge connections to the TARGET wall
  // Use GEOMETRIC PROXIMITY as the primary check - much more reliable than endpoint name matching
  for (const [wallId, wall] of floorplannerStore.wallsMap) {
    if (wallId === sourceWall.id || wallId === bestTargetWallId) continue;
    if (multiJointWallIds.has(wallId)) continue;

    if (wall.edgeConnections) {
      for (const ec of wall.edgeConnections) {
        // Check if this edge connection targets the same wall (the target we're connecting to)
        if (ec.targetWallId === bestTargetWallId) {
          // Use geometric proximity: check if the edge connection's target point is near where we're connecting
          // The targetEdgePoint is the actual point on the target wall where the connection is made
          const ecTargetPoint = ec.targetEdgePoint;
          if (ecTargetPoint) {
            const distToSnapPos = ecTargetPoint.distanceTo(sourceEndpointPos);
            if (distToSnapPos < multiJointTolerance) {
              multiJointWallIds.add(wallId);
              multiJointEndpointByWallId.set(wallId, ec.sourceEndpoint);
              break;
            }
          }

          // Fallback: also check endpoint position if targetEdgePoint is not available
          const ecTargetEndpointPos = ec.targetEndpoint === 'start' ? targetWall.start :
            ec.targetEndpoint === 'end' ? targetWall.end : null;
          if (ecTargetEndpointPos) {
            const distToTargetEndpoint = ecTargetEndpointPos.distanceTo(targetEndpointPos);
            if (distToTargetEndpoint < multiJointTolerance) {
              multiJointWallIds.add(wallId);
              multiJointEndpointByWallId.set(wallId, ec.sourceEndpoint);
              break;
            }
          }
        }
      }
    }

    // Also check by physical endpoint proximity - if this wall's endpoint is near our snap position
    if (!multiJointWallIds.has(wallId)) {
      const distToStart = wall.start.distanceTo(sourceEndpointPos);
      const distToEnd = wall.end.distanceTo(sourceEndpointPos);
      if (distToStart < multiJointTolerance || distToEnd < multiJointTolerance) {
        // This wall's endpoint is near where we're connecting - check if it has any connection to target
        if (wall.edgeConnections?.some(ec => ec.targetWallId === bestTargetWallId)) {
          multiJointWallIds.add(wallId);
          multiJointEndpointByWallId.set(wallId, distToStart <= distToEnd ? "start" : "end");
        }
      }
    }
  }

  // METHOD 2: Also check if target wall has edge connections FROM it to other walls at this endpoint
  if (targetWall.edgeConnections) {
    for (const existingConn of targetWall.edgeConnections) {
      const connectedWallId = existingConn.targetWallId;
      if (connectedWallId === sourceWall.id || multiJointWallIds.has(connectedWallId)) continue;

      // Use geometric proximity to check if connection is at the same location
      const ecSourcePoint = existingConn.sourceEdgePoint;
      if (ecSourcePoint) {
        const distToSnapPos = ecSourcePoint.distanceTo(sourceEndpointPos);
        if (distToSnapPos < multiJointTolerance) {
          multiJointWallIds.add(connectedWallId);
          const connectedWall = floorplannerStore.wallsMap.get(connectedWallId);
          const connectedEndpoint = existingConn.targetEndpoint === "start" || existingConn.targetEndpoint === "end"
            ? existingConn.targetEndpoint
            : connectedWall
              ? resolveMultiJointEndpointForWall(connectedWall, sourceJointReferencePoints, multiJointTolerance)
              : null;
          if (connectedEndpoint) {
            multiJointEndpointByWallId.set(connectedWallId, connectedEndpoint);
          }
          continue;
        }
      }

      // Fallback: check by endpoint name
      const ecSourceEndpointPos = existingConn.sourceEndpoint === 'start' ? targetWall.start :
        existingConn.sourceEndpoint === 'end' ? targetWall.end : null;
      if (ecSourceEndpointPos) {
        const distToTargetEndpoint = ecSourceEndpointPos.distanceTo(targetEndpointPos);
        if (distToTargetEndpoint < multiJointTolerance) {
          multiJointWallIds.add(connectedWallId);
          const connectedWall = floorplannerStore.wallsMap.get(connectedWallId);
          const connectedEndpoint = existingConn.targetEndpoint === "start" || existingConn.targetEndpoint === "end"
            ? existingConn.targetEndpoint
            : connectedWall
              ? resolveMultiJointEndpointForWall(connectedWall, sourceJointReferencePoints, multiJointTolerance)
              : null;
          if (connectedEndpoint) {
            multiJointEndpointByWallId.set(connectedWallId, connectedEndpoint);
          }
        }
      }
    }
  }

  // METHOD 2: Find other walls whose endpoints are near the source endpoint position
  // These could form a multi-joint with the source wall (for walls not yet connected)
  for (const [wallId, wall] of floorplannerStore.wallsMap) {
    if (multiJointWallIds.has(wallId)) continue;

    // Check if this wall's start/end or rendered endpoint extension is near the source endpoint
    const distToStart = wall.start.distanceTo(sourceEndpointPos);
    const distToEnd = wall.end.distanceTo(sourceEndpointPos);
    const hasEffectiveEndpointNearSource = sourceJointReferencePoints.some(
      (referencePoint) => !!findEffectiveEndpointSnap(wall, referencePoint, multiJointTolerance),
    );

    if (distToStart < multiJointTolerance || distToEnd < multiJointTolerance || hasEffectiveEndpointNearSource) {
      const jointEndpoint = resolveMultiJointEndpointForWall(wall, sourceJointReferencePoints, multiJointTolerance);
      if (jointEndpoint) {
        multiJointEndpointByWallId.set(wallId, jointEndpoint);
      }

      // This wall is near the source endpoint - try to snap to it
      for (const sidePair of EDGE_SIDE_PAIR_OPTIONS) {
        const sourceReferencePosition = getSourceEdgeReferencePosition(finalSourceWallAtJoint, sourceEndpoint, sidePair);
        const snapResult = snapToWallEdge(
          sourceWall.id,
          sourceEndpoint,
          sourceEndpointPos,
          wallId,
          sidePair,
          finalSourceWallAtJoint,
          sourceReferencePosition,
        );
        if (snapResult.snapped && snapResult.edgeConnection && snapResult.snappingPosition) {
          multiJointCandidates.push({
            snapResult: {
              snapped: snapResult.snapped,
              edgeConnection: snapResult.edgeConnection,
              targetEndpoint: snapResult.targetEndpoint!,
              snappingPosition: snapResult.snappingPosition,
            },
            targetWall: wall,
          });
          multiJointWallIds.add(wallId);
          break; // Only need one side to work
        }
      }
    }
  }

  // Determine if this is a multi-joint (3+ walls total at this location)
  // multiJointWallIds includes: source wall, target wall, and any walls with existing connections
  const isMultiJoint = multiJointWallIds.size >= 3;
  const multiJointReferencePoints = [
    ...sourceJointReferencePoints,
    targetEndpointPos,
  ];

  if (isMultiJoint) {
    for (const wallId of multiJointWallIds) {
      if (multiJointEndpointByWallId.has(wallId)) {
        continue;
      }

      const wall = wallId === sourceWall.id
        ? finalSourceWallAtJoint
        : floorplannerStore.wallsMap.get(wallId);
      if (!wall) {
        continue;
      }

      const endpoint = resolveMultiJointEndpointForWall(wall, multiJointReferencePoints, multiJointTolerance);
      if (endpoint) {
        multiJointEndpointByWallId.set(wallId, endpoint);
      }
    }
  }

  const committedDirectionalConnectionKeys = collectDirectionalConnectionKeys(multiJointWallIds);
  const addEdgeConnectionIfMissing = (connection: WallEdgeConnection) => {
    const connectionKey = getDirectionalConnectionKey(connection.sourceWallId, connection.targetWallId);
    if (committedDirectionalConnectionKeys.has(connectionKey)) {
      return;
    }

    floorplannerStore.addEdgeConnection(
      connection.sourceWallId,
      connection.targetWallId,
      connection.sourceEndpoint,
      connection.targetEndpoint,
      connection,
    );
    rememberCommittedEdgeConnection(committedDirectionalConnectionKeys, connection);
  };

  // Record edge connections to ALL multi-joint candidates (walls we're snapping to)
  // This creates the full multi-joint topology
  for (const candidate of multiJointCandidates) {
    // Skip creating VoidFillMetadata for multi-joints - the joint polygon system handles it
    const connectionToAdd = { ...candidate.snapResult.edgeConnection };
    if (isMultiJoint) {
      // This is a multi-joint (3+ walls total)
      // Clear any voidFill metadata as the multi-joint polygon will handle filling
      delete connectionToAdd.voidFill;
    }

    addEdgeConnectionIfMissing(connectionToAdd);
  }

  if (isMultiJoint) {
    const participantIds = Array.from(multiJointWallIds);
    for (const sourceWallId of participantIds) {
      const participantSourceWall = sourceWallId === sourceWall.id
        ? finalSourceWallAtJoint
        : floorplannerStore.wallsMap.get(sourceWallId);
      const participantSourceEndpoint = multiJointEndpointByWallId.get(sourceWallId);
      if (!participantSourceWall || !participantSourceEndpoint) {
        continue;
      }

      for (const targetWallId of participantIds) {
        if (
          targetWallId === sourceWallId ||
          committedDirectionalConnectionKeys.has(getDirectionalConnectionKey(sourceWallId, targetWallId))
        ) {
          continue;
        }

        const supplementalConnection = findSupplementalMultiJointConnection(
          participantSourceWall,
          participantSourceEndpoint,
          targetWallId,
        );
        if (!supplementalConnection) {
          continue;
        }

        delete supplementalConnection.voidFill;
        addEdgeConnectionIfMissing(supplementalConnection);
      }
    }
  }

  // Clear voidFill from any existing connections at this joint
  // since the multi-joint system will handle filling
  // Use multiJointWallIds which includes ALL walls at this joint (including those with existing connections)
  if (isMultiJoint) {
    clearVoidFillForMultiJoint(
      Array.from(multiJointWallIds),
      sourceEndpoint
    );
  }

  return {
    snapped: true,
    targetId: bestCandidate.targetWall.id,
    targetObject: bestCandidate.targetWall,
    position: bestCandidate.snapResult.snappingPosition.clone(),
  };
};

const computeCornerTolerance = (sourceWall?: WallType, targetWall?: WallType): number => {
  // Use editor's snapping tolerance to allow easier connections
  // The previous fixed 2mm (0.002) was too strict and prevented connections unless manually precise
  const editorStore = getEditorStore();
  return getInteractionSnappingTolerance(editorStore) / 2;
};

const checkAgainstObject = (
  source: string,
  target: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
  options: { allowDuringDeferred?: boolean; forceCenterMode?: boolean } = {},
): any => {
  // This function handles center-based snapping for mid-point connections
  // Edge-based snapping for endpoints is handled by snapToOtherObjectsEdgeBased()

  const sourceObject = floorplannerStore.findObjectId(source);
  const targetObject = floorplannerStore.findObjectId(target);
  if (sourceObject && targetObject) {
    // Make sure that the target object is not already connected to the source object on any end
    const alreadyConnected = targetObject.centerConnections?.find(
      (c: { id: string; }) => c.id === sourceObject.id,
    );
    if (!alreadyConnected) {
      // Check if the target object is not the same as the source object
      if (targetObject.id !== sourceObject.id) {
        if (!targetObject.start || !targetObject.end) {
          return null;
        }

        // Get editorStore before using it for tolerance checks
        const editorStore = getEditorStore();
        if (!editorStore) return null;

        // Replace endpoint-only early-exit with segment-distance check
        // Use a search radius derived from tolerance (considerably larger than tolerance for broader search)
        // but not hard-coded - derive from tolerance for consistency
        const snappingTolerance = getInteractionSnappingTolerance(editorStore);
        const searchRadius = snappingTolerance * 30; // 30x tolerance for broader search
        const seg = new THREE.Line3(new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0), new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0));
        const cp = new THREE.Vector3();
        seg.closestPointToPoint(new THREE.Vector3(edge.x, edge.y, 0), true, cp);
        const dist = edge.distanceTo(new THREE.Vector2(cp.x, cp.y));
        if (dist > searchRadius) return null;
        // Create a line from the target object to calculate the closest point against
        const targetLine = new THREE.Line3(
          new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0),
          new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0),
        );
        if (!edge || !edge.x || !edge.y) {
          return null;
        }
        const closestEdge = new THREE.Vector3();
        targetLine.closestPointToPoint(
          new THREE.Vector3(edge.x, edge.y, 0),
          true,
          closestEdge,
        );
        // editorStore already retrieved above
        // Check if the closest point is within the snap threshold
        if (
          edge.distanceTo(
            new THREE.Vector2(closestEdge.x, closestEdge.y),
          ) < snappingTolerance
        ) {
          // If the closestEdge is near the target object start, we snap to the start
          if (closestEdge.distanceTo(new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0)) < snappingTolerance) {
            closestEdge.set(targetObject.start.x, targetObject.start.y, 0);
          }
          // If the closestEdge is near the target object end, we snap to the end
          if (closestEdge.distanceTo(new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0)) < snappingTolerance) {
            closestEdge.set(targetObject.end.x, targetObject.end.y, 0);
          }

          // Calculate snapped position and metadata
          const snappedPosition = new THREE.Vector2(closestEdge.x, closestEdge.y);

          // Get the position of the connection on the other object, always from the start
          let connectionPos = targetObject.start.distanceTo(snappedPosition);
          // If connectionPos is the same as the wall end (or larger), we make sure it is the wall end
          if (editorStore && connectionPos + snappingTolerance >= targetObject.start.distanceTo(targetObject.end)) {
            connectionPos = WallConnectionEnd;
          }
          // If connectionPos is the same as the wall start (or smaller), we make sure it is the wall start
          if (editorStore && connectionPos - snappingTolerance <= 0) {
            connectionPos = WallConnectionStart;
          }

          // Check if we're in deferred mode and should not mutate the store
          const isDeferredMode = floorplannerStore.deferConnectionOperations && !options.allowDuringDeferred;

          if (!isDeferredMode) {
            // Save the original object length and angle
            const originalObjectLength = sourceObject.start.distanceTo(sourceObject.end);
            const originalObjectAngle = Math.atan2(
              sourceObject.end.y - sourceObject.start.y,
              sourceObject.end.x - sourceObject.start.x,
            );

            // Since we are in snapping distance, we need to move the whole object (to snap)
            if (snappingEnd === WallConnectionStart) {
              const start = snappedPosition.clone();
              const end = sourceObject.end.clone();
              if (updateOtherEnd) {
                end.set(
                  start.x + originalObjectLength * Math.cos(originalObjectAngle),
                  start.y + originalObjectLength * Math.sin(originalObjectAngle),
                );
              }
              floorplannerStore.updateObjectPosition(sourceObject.id, start, end);
            } else {
              const start = sourceObject.start.clone();
              const end = snappedPosition.clone();
              if (updateOtherEnd) {
                start.set(
                  end.x - originalObjectLength * Math.cos(originalObjectAngle),
                  end.y - originalObjectLength * Math.sin(originalObjectAngle),
                );
              }
              floorplannerStore.updateObjectPosition(sourceObject.id, start, end);
            }

            // Update the connection using center-based approach (for mid-point connections)
            // Note: Endpoint connections are handled by edge-based snapping above
            // This path is for mid-point connections only (connectionPos !== WallConnectionStart && connectionPos !== WallConnectionEnd)
            floorplannerStore.addConnection(
              sourceObject.id,
              targetObject.id,
              snappingEnd,
              connectionPos,
              { mode: "center" }
            );
          }

          // Return SnappingResult with all necessary data
          return {
            snapped: true,
            position: snappedPosition,
            targetId: targetObject.id,
            targetObject: targetObject,
            connectionPos: connectionPos,
            snappingEnd: snappingEnd
          };
        }
      }
    }
  }
  return null;
}

export const snapToAlignmentLines = (
  source: string,
  snappingEnd: number,
  edge: THREE.Vector2,
): AlignmentSnapResult => {
  const editorStore = getEditorStore();
  if (!editorStore) {
    return {
      snapped: false,
      snappedX: false,
      snappedY: false,
      start: edge.clone(),
      end: edge.clone(),
    };
  }

  const object = floorplannerStore.findObjectId(source);
  const baseStart = object ? object.start.clone() : new THREE.Vector2(edge.x, edge.y);
  const baseEnd = object ? object.end.clone() : baseStart.clone();

  if (editorStore.isShiftPressed || !object) {
    return {
      snapped: false,
      snappedX: false,
      snappedY: false,
      start: baseStart,
      end: baseEnd,
    };
  }

  // Get previous alignment state for hysteresis
  const dragState = floorplannerStore.wallDragState.get(source);
  const previousKey = snappingEnd === WallConnectionStart ? "start" : "end";
  const previous = dragState?.alignmentSnap?.[previousKey];

  const result = calculateAlignmentSnap(object, snappingEnd, edge, {
    previous,
  });

  // Update the stored alignment state for next iteration
  if (dragState) {
    if (!dragState.alignmentSnap) {
      dragState.alignmentSnap = {};
    }
    dragState.alignmentSnap[previousKey] = {
      snappedX: result.snappedX,
      snappedY: result.snappedY,
      targetX: result.targetX,
      targetY: result.targetY,
    };
  }

  if (result.snapped) {
    floorplannerStore.updateObjectPosition(object.id, result.start.clone(), result.end.clone());
  }

  return result;
}

export const getSnappingPoint = (
  sourceObjectId: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
): THREE.Vector2 | null => {
  const editorStore = getEditorStore();
  if (!editorStore || editorStore.isShiftPressed) {
    return null;
  }

  const sourceObject = floorplannerStore.findObjectId(sourceObjectId);
  const isWallObject = sourceObject?.type === "wall";

  // Try edge-based snapping first for endpoint connections (walls with ALT released)
  const shouldUseEdgeSnapping = isWallObject && !editorStore?.isAltPressed;
  if (shouldUseEdgeSnapping) {
    const sourceEndpoint = snappingEnd === WallConnectionStart ? "start" : "end";
    const edgeSnapPreview = getEdgeSnappingPositionForDrag(sourceObjectId, sourceEndpoint, edge);
    if (edgeSnapPreview) {
      return edgeSnapPreview.snappingPosition.clone();
    }
  }

  const allowCenterSnapping = !isWallObject || !!editorStore?.isAltPressed;

  if (allowCenterSnapping) {
    const snappingPoint = getSnappingPointFromOtherObjects(
      sourceObjectId,
      snappingEnd,
      edge,
      updateOtherEnd,
      allowCenterSnapping
    );
    if (snappingPoint) {
      return snappingPoint;
    }
  }

  // Attempt to get snapping point from alignment lines
  const snappingPointAlignment = getSnappingPointFromAlignmentLines(
    sourceObjectId,
    snappingEnd,
    edge
  );
  if (snappingPointAlignment) {
    return snappingPointAlignment;
  }

  // No snapping occurred
  return null;
};

const getSnappingPointFromOtherObjects = (
  sourceObjectId: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
  allowCenterSnapping: boolean = true,
): THREE.Vector2 | null => {
  if (!allowCenterSnapping) {
    return null;
  }
  const object = floorplannerStore.findObjectId(sourceObjectId);
  if (object) {
    // Ensure the snappingEnd is free from any connections
    if (object.centerConnections) {
      const connection = object.centerConnections.find(
        (c) => c.sourcePosition === snappingEnd,
      );
      if (connection) {
        return null;
      }
    }

    // Check against walls with spatial optimization
    const wallsToCheck = floorplannerStore.enableSpatialOptimization
      ? getNearbyWalls(edge, 2.0)
      : Array.from(floorplannerStore.wallsMap.values());

    for (const targetWall of wallsToCheck) {
      const snappingPoint = getSnappingPointAgainstObject(
        object.id,
        targetWall.id,
        snappingEnd,
        edge,
        updateOtherEnd,
      );
      if (snappingPoint) {
        return snappingPoint;
      }
    }

    // Check against single lines
    for (const targetLine of floorplannerStore.singleLinesMap.values()) {
      const snappingPoint = getSnappingPointAgainstObject(
        object.id,
        targetLine.id,
        snappingEnd,
        edge,
        updateOtherEnd,
      );
      if (snappingPoint) {
        return snappingPoint;
      }
    }

    // Check against ruler lines
    for (const targetRuler of floorplannerStore.rulerLinesMap.values()) {
      const snappingPoint = getSnappingPointAgainstObject(
        object.id,
        targetRuler.id,
        snappingEnd,
        edge,
        updateOtherEnd,
      );
      if (snappingPoint) {
        return snappingPoint;
      }
    }
  }
  return null;
};

const getSnappingPointAgainstObject = (
  sourceId: string,
  targetId: string,
  snappingEnd: number,
  edge: THREE.Vector2,
  updateOtherEnd: boolean = true,
): THREE.Vector2 | null => {
  const editor = getEditorStore();
  const snappingTolerance = getInteractionSnappingTolerance(editor);

  const sourceObject = floorplannerStore.findObjectId(sourceId);
  const targetObject = floorplannerStore.findObjectId(targetId);
  if (sourceObject && targetObject) {
    // Ensure the target object is not already connected to the source object
    const alreadyConnected = targetObject.centerConnections?.find(
      (c: { id: string }) => c.id === sourceObject.id,
    );
    if (!alreadyConnected && targetObject.id !== sourceObject.id) {
      // Create a line from the target object to calculate the closest point
      const targetLine = new THREE.Line3(
        new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0),
        new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0),
      );
      const closestEdge = new THREE.Vector3();
      targetLine.closestPointToPoint(
        new THREE.Vector3(edge.x, edge.y, 0),
        true,
        closestEdge,
      );
      // Check if the closest point is within the snap threshold
      if (
        edge.distanceTo(new THREE.Vector2(closestEdge.x, closestEdge.y)) <
        snappingTolerance
      ) {
        // Snap to the closest end if within tolerance
        if (
          closestEdge.distanceTo(
            new THREE.Vector3(targetObject.start.x, targetObject.start.y, 0),
          ) < snappingTolerance
        ) {
          closestEdge.set(targetObject.start.x, targetObject.start.y, 0);
        } else if (
          closestEdge.distanceTo(
            new THREE.Vector3(targetObject.end.x, targetObject.end.y, 0),
          ) < snappingTolerance
        ) {
          closestEdge.set(targetObject.end.x, targetObject.end.y, 0);
        }
        // Return the snapping point
        return new THREE.Vector2(closestEdge.x, closestEdge.y);
      }
    }
  }
  return null;
};


export const getSnappingPointFromAlignmentLines = (
  sourceId: string,
  _snappingEnd: number,
  edge: THREE.Vector2,
): THREE.Vector2 | null => {
  const editorStore = getEditorStore();
  if (!editorStore || editorStore.isShiftPressed) {
    return null;
  }

  const sourceObject = floorplannerStore.findObjectId(sourceId);
  if (!sourceObject) {
    return null;
  }

  // Get previous alignment state for hysteresis
  const dragState = floorplannerStore.wallDragState.get(sourceId);
  const previousKey = _snappingEnd === WallConnectionStart ? "start" : "end";
  const previous = dragState?.alignmentSnap?.[previousKey];

  const result = calculateAlignmentSnap(sourceObject, _snappingEnd, edge, {
    previous,
  });

  // Update the stored alignment state for next iteration
  if (dragState) {
    if (!dragState.alignmentSnap) {
      dragState.alignmentSnap = {};
    }
    dragState.alignmentSnap[previousKey] = {
      snappedX: result.snappedX,
      snappedY: result.snappedY,
      targetX: result.targetX,
      targetY: result.targetY,
    };
  }

  if (!result.snapped) {
    return null;
  }

  return _snappingEnd === WallConnectionStart
    ? result.start.clone()
    : result.end.clone();
};

export interface EdgeSnappingPreviewResult {
  snappingPosition: THREE.Vector2;
  edgeConnection: WallEdgeConnection;
  targetEndpoint: "start" | "end" | "middle";
}

export interface EdgeSnappingSourceOverrides {
  start: THREE.Vector2;
  end: THREE.Vector2;
  controlPoint?: THREE.Vector2;
}

export const getEdgeSnappingPositionForDrag = (
  sourceWallId: string,
  sourceEndpoint: "start" | "end",
  edgePosition: THREE.Vector2,
  sourceOverrides?: EdgeSnappingSourceOverrides,
): EdgeSnappingPreviewResult | null => {
  const editorStore = getEditorStore();
  // Early return if center snapping mode is active (ALT/OPTION key pressed)
  if (editorStore?.isAltPressed) {
    return null;
  }

  const baseSourceWall = floorplannerStore.wallsMap.get(sourceWallId);
  if (!baseSourceWall) {
    return null;
  }

  const sourceWall = sourceOverrides
    ? cloneWallWithGeometry(baseSourceWall, sourceOverrides)
    : baseSourceWall;

  // Get edge snap candidates
  const candidates = getEdgeSnapCandidates(sourceWallId, sourceEndpoint, edgePosition, sourceWall);

  if (candidates.length === 0) {
    return null;
  }

  // Sort by distance and return the closest
  const sorted = candidates.sort((a, b) =>
    compareEdgeSnapPreference(edgePosition, a, b)
  );

  const best = sorted[0];
  return {
    snappingPosition: best.snappingPosition.clone(),
    edgeConnection: best.edgeConnection,
    targetEndpoint: best.targetEndpoint,
  };
};

const calculateEdgeConnectionPoint = (
  wall: WallType,
  endpoint: "start" | "end",
  targetWall: WallType,
  targetEndpoint: "start" | "end",
): THREE.Vector2 => {
  const otherAngle = getWallAngle(targetWall);
  const edgePoint = calculateWallEdgePoint(wall, endpoint, otherAngle);
  return edgePoint.edgePosition;
};

/**
 * Check if a wall's endpoint is embedded (visually hidden) inside another wall.
 * This is used to filter alignment lines - embedded endpoints should not generate
 * alignment lines since they are not visually visible edges.
 * 
 * An endpoint is considered "embedded" if it has a "middle" connection to another wall
 * (a T-junction), meaning the endpoint connects to the CENTER of another wall, not
 * to that wall's start or end.
 * 
 * This checks both centerConnections and edgeConnections for T-junctions.
 * 
 * @param wall - The wall to check
 * @param endpoint - Which endpoint to check ("start" or "end")
 * @returns true if the endpoint is embedded inside another wall's body
 */
export const isWallEndpointEmbedded = (
  wall: WallType,
  endpoint: "start" | "end",
): boolean => {
  // Check centerConnections for middle connections
  if (wall.centerConnections && wall.centerConnections.length > 0) {
    const sourcePosition = endpoint === "start" ? WallConnectionStart : WallConnectionEnd;

    for (const conn of wall.centerConnections) {
      if (conn.sourcePosition === sourcePosition) {
        // If targetPosition is NOT WallConnectionStart or WallConnectionEnd, 
        // it's a middle connection (T-junction)
        if (conn.targetPosition !== WallConnectionStart && conn.targetPosition !== WallConnectionEnd) {
          return true;
        }
      }
    }
  }

  // Also check edgeConnections for middle connections (T-junctions)
  if (wall.edgeConnections && wall.edgeConnections.length > 0) {
    for (const conn of wall.edgeConnections) {
      if (conn.sourceEndpoint === endpoint && conn.targetEndpoint === "middle") {
        return true;
      }
    }
  }

  return false;
};

/**
 * Get the visible edge positions for a wall, excluding embedded endpoints.
 * For walls with T-junction connections (middle connections), the embedded
 * endpoint is excluded, and only the visible edges are returned.
 * 
 * @param wall - The wall to get visible edges for
 * @returns Object with start and end visibility flags and positions
 */
export const getVisibleWallEdges = (
  wall: WallType,
): {
  startVisible: boolean;
  endVisible: boolean;
  start: THREE.Vector2;
  end: THREE.Vector2;
} => {
  const startEmbedded = isWallEndpointEmbedded(wall, "start");
  const endEmbedded = isWallEndpointEmbedded(wall, "end");

  return {
    startVisible: !startEmbedded,
    endVisible: !endEmbedded,
    start: wall.start.clone(),
    end: wall.end.clone(),
  };
};
